import * as query from './alan_api';
import * as application from '../../libraries/model_lib/types/application/alan_api';
import * as parameters from '../parameters/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries:Map<string, () => T['node']>;

	constructor (entries:{[key:string]:T['init']}, protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, () => {
			const loaded_entry = this.initialize(this.parent, entry_key, entries[entry_key])
			this._entries.set(entry_key, () => loaded_entry);
			return loaded_entry;
		}]));
	}

	protected abstract initialize(parent: P, key: string, obj: T['init']): T['node'];
	protected abstract finalize(obj: T['node'], detach?: boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator](): IterableIterator<[string, T['node']]> {
		const iterator = this._entries.entries();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: [next.value[0], next.value[1]()]
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	entries(): IterableIterator<[string, T['node']]> {
		return this[Symbol.iterator]();
	}
	values(): IterableIterator<T['node']> {
		const iterator = this._entries.values();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: next.value()
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	keys(): IterableIterator<string> {
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		return Array.from(this.entries());
	}
	map<RT>(callback: (value: T['node']) => RT): Record<string, RT> {
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node'] | undefined {
		return this._entries.get(key)?.();
	}
	has(key: string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key: string, onExists: (($: T['node']) => RT) | Exclude<RT, Function>, onNotExists: (() => RT) | Exclude<RT, Function>): RT {
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry()) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cquery
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cquery|undefined;
	public abstract get root():Cquery;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
export type Ttext_value = {
	'type':['dynamic', Tdynamic__type__text_value]|['static', Tstatic__type__text_value];
};
export class Ctext_value extends AlanNode {
	public readonly properties:{
		readonly type:Ctext_value.Dtype
	};
	constructor(init:Ttext_value, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext_value.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic__type__text_value = {
	'parameter':string;
};
export class Cdynamic__type__text_value extends AlanNode {
	public readonly properties:{
		readonly parameter:Cdynamic__type__text_value.Dparameter
	};
	constructor(init:Tdynamic__type__text_value, public parent:Ctext_value) {
		super();
		const $this = this;
		this.properties = {
			parameter: new Cdynamic__type__text_value.Dparameter(init['parameter'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'dynamic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type__text_value = {
	'value':string;
};
export class Cstatic__type__text_value extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	constructor(init:Tstatic__type__text_value, public parent:Ctext_value) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext_format = {
	'type':'text'|['text', {}];
};
export class Ctext_format extends AlanNode {
	public readonly properties:{
		readonly type:Ctext_format.Dtype
	};
	constructor(init:Ttext_format, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext_format.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Ttext__type__text_format = {
};
export class Ctext__type__text_format extends AlanNode {
	constructor(init:Ttext__type__text_format, public parent:Ctext_format) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext_expression = {
	'type':['property', Tproperty__type__text_expression]|['value', Tvalue__type__text_expression];
};
export class Ctext_expression extends AlanNode {
	public readonly properties:{
		readonly type:Ctext_expression.Dtype
	};
	constructor(init:Ttext_expression, public location:AlanNode, public input: {
		node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tproperty__type__text_expression = {
	'attribute':string;
	'node selection':Tnode_path;
};
export class Cproperty__type__text_expression extends AlanNode {
	public readonly properties:{
		readonly attribute:Cproperty__type__text_expression.Dattribute,
		readonly node_selection:Cnode_path
	};
	constructor(init:Tproperty__type__text_expression, public parent:Ctext_expression) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cproperty__type__text_expression.Dattribute(init['attribute'], $this),
			node_selection: new Cproperty__type__text_expression.Dnode_selection(init['node selection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalue__type__text_expression = {
	'value':Ttext_value;
};
export class Cvalue__type__text_expression extends AlanNode {
	public readonly properties:{
		readonly value:Ctext_value
	};
	constructor(init:Tvalue__type__text_expression, public parent:Ctext_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cvalue__type__text_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstategroup_expression = {
	'attribute':string;
	'node selection':Tnode_path;
};
export class Cstategroup_expression extends AlanNode {
	public readonly properties:{
		readonly attribute:Cstategroup_expression.Dattribute,
		readonly node_selection:Cnode_path
	};
	public readonly output:{
		stategroup: () => application.Cstate_group;
	} = {
		stategroup: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.attribute?.inferences.stategroup()).result!)
	};
	constructor(init:Tstategroup_expression, public location:AlanNode, public input: {
		node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cstategroup_expression.Dattribute(init['attribute'], $this),
			node_selection: new Cstategroup_expression.Dnode_selection(init['node selection'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tstate_format = {
	'type':'text'|['text', {}];
};
export class Cstate_format extends AlanNode {
	public readonly properties:{
		readonly type:Cstate_format.Dtype
	};
	constructor(init:Tstate_format, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cstate_format.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Ttext__type__state_format = {
};
export class Ctext__type__state_format extends AlanNode {
	constructor(init:Ttext__type__state_format, public parent:Cstate_format) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_filter_list = {
	'context':'query context'|['query context', {}]|'root'|['root', {}];
	'has alternative':'no'|['no', {}]|['yes', Tyes__has_alternative];
	'referenced node':Tid_path;
};
export class Creference_filter_list extends AlanNode {
	public readonly properties:{
		readonly context:Creference_filter_list.Dcontext,
		readonly has_alternative:Creference_filter_list.Dhas_alternative,
		readonly referenced_node:Cid_path & { readonly inferences: {
			entity_constraint: () => application.Centity__application;
		} }
	};
	constructor(init:Treference_filter_list, public location:AlanNode, public input: {
		entity_node: () => application.Centity__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			context: new Creference_filter_list.Dcontext(init['context'], $this),
			has_alternative: new Creference_filter_list.Dhas_alternative(init['has alternative'], $this),
			referenced_node: new Creference_filter_list.Dreferenced_node(init['referenced node'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tquery_context = {
};
export class Cquery_context extends AlanNode {
	constructor(init:Tquery_context, public parent:Creference_filter_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'query context'`; }
	public get entity() { return this.parent.entity; }
}
export type Troot = {
};
export class Croot extends AlanNode {
	constructor(init:Troot, public parent:Creference_filter_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'context'?'root'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_alternative = {
};
export class Cno__has_alternative extends AlanNode {
	constructor(init:Tno__has_alternative, public parent:Creference_filter_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has alternative'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_alternative = {
	'alternative':Treference_filter_list;
};
export class Cyes__has_alternative extends AlanNode {
	public readonly properties:{
		readonly alternative:Creference_filter_list
	};
	constructor(init:Tyes__has_alternative, public parent:Creference_filter_list) {
		super();
		const $this = this;
		this.properties = {
			alternative: new Cyes__has_alternative.Dalternative(init['alternative'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has alternative'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_value = {
	'type':['dynamic', Tdynamic__type__number_value]|['static', Tstatic__type__number_value];
};
export class Cnumber_value extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber_value.Dtype
	};
	constructor(init:Tnumber_value, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber_value.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic__type__number_value = {
	'parameter':string;
};
export class Cdynamic__type__number_value extends AlanNode {
	public readonly properties:{
		readonly parameter:Cdynamic__type__number_value.Dparameter
	};
	constructor(init:Tdynamic__type__number_value, public parent:Cnumber_value) {
		super();
		const $this = this;
		this.properties = {
			parameter: new Cdynamic__type__number_value.Dparameter(init['parameter'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'dynamic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type__number_value = {
	'value':number;
};
export class Cstatic__type__number_value extends AlanNode {
	public readonly properties:{
		readonly value:Cstatic__type__number_value.Dvalue
	};
	constructor(init:Tstatic__type__number_value, public parent:Cnumber_value) {
		super();
		const $this = this;
		this.properties = {
			value: new Cstatic__type__number_value.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_format = {
	'type':'date'|['date', {}]|'datetime'|['datetime', {}]|['decimal', Tdecimal]|'number'|['number', {}]|'time'|['time', {}];
};
export class Cnumber_format extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber_format.Dtype
	};
	constructor(init:Tnumber_format, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber_format.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdate = {
};
export class Cdate extends AlanNode {
	constructor(init:Tdate, public parent:Cnumber_format) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'date'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdatetime = {
};
export class Cdatetime extends AlanNode {
	constructor(init:Tdatetime, public parent:Cnumber_format) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'datetime'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdecimal = {
	'shift':number;
};
export class Cdecimal extends AlanNode {
	public readonly properties:{
		readonly shift:Cdecimal.Dshift
	};
	constructor(init:Tdecimal, public parent:Cnumber_format) {
		super();
		const $this = this;
		this.properties = {
			shift: new Cdecimal.Dshift(init['shift'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'decimal'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__number_format = {
};
export class Cnumber__type__number_format extends AlanNode {
	constructor(init:Tnumber__type__number_format, public parent:Cnumber_format) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttime = {
};
export class Ctime extends AlanNode {
	constructor(init:Ttime, public parent:Cnumber_format) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'time'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_filter_expression = {
	'operator':['equals', Tequals]|['range', Trange];
};
export class Cnumber_filter_expression extends AlanNode {
	public readonly properties:{
		readonly operator:Cnumber_filter_expression.Doperator
	};
	constructor(init:Tnumber_filter_expression, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			operator: new Cnumber_filter_expression.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tequals = {
	'criterium':Tnumber_value;
};
export class Cequals extends AlanNode {
	public readonly properties:{
		readonly criterium:Cnumber_value
	};
	constructor(init:Tequals, public parent:Cnumber_filter_expression) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cequals.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'equals'`; }
	public get entity() { return this.parent.entity; }
}
export type Trange = {
	'greater than':'no'|['no', {}]|['yes', Tyes__greater_than];
	'less than':'no'|['no', {}]|['yes', Tyes__less_than];
};
export class Crange extends AlanNode {
	public readonly properties:{
		readonly greater_than:Crange.Dgreater_than,
		readonly less_than:Crange.Dless_than
	};
	constructor(init:Trange, public parent:Cnumber_filter_expression) {
		super();
		const $this = this;
		this.properties = {
			greater_than: new Crange.Dgreater_than(init['greater than'], $this),
			less_than: new Crange.Dless_than(init['less than'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'range'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__greater_than = {
};
export class Cno__greater_than extends AlanNode {
	constructor(init:Tno__greater_than, public parent:Crange) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'greater than'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__greater_than = {
	'criterium':Tnumber_value;
};
export class Cyes__greater_than extends AlanNode {
	public readonly properties:{
		readonly criterium:Cnumber_value
	};
	constructor(init:Tyes__greater_than, public parent:Crange) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cyes__greater_than.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'greater than'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__less_than = {
};
export class Cno__less_than extends AlanNode {
	public readonly inferences:{
		must_be_greater_than: () => query.Cyes__greater_than
	} = {
		must_be_greater_than: cache((detach:boolean) => {
			const query__number_filter_expression__operator__range__less_than__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => {
					if (context?.properties.greater_than.state.name === 'yes') {
						return resolve(context.properties.greater_than.state.node as query.Cyes__greater_than).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__less_than, public parent:Crange) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'less than'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__less_than = {
	'criterium':Tnumber_value;
};
export class Cyes__less_than extends AlanNode {
	public readonly properties:{
		readonly criterium:Cnumber_value
	};
	constructor(init:Tyes__less_than, public parent:Crange) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cyes__less_than.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'less than'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_expression = {
	'type':['property', Tproperty__type__number_expression]|['value', Tvalue__type__number_expression];
};
export class Cnumber_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber_expression.Dtype
	};
	constructor(init:Tnumber_expression, public location:AlanNode, public input: {
		node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tproperty__type__number_expression = {
	'attribute':string;
	'node selection':Tnode_path;
};
export class Cproperty__type__number_expression extends AlanNode {
	public readonly properties:{
		readonly attribute:Cproperty__type__number_expression.Dattribute,
		readonly node_selection:Cnode_path
	};
	constructor(init:Tproperty__type__number_expression, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cproperty__type__number_expression.Dattribute(init['attribute'], $this),
			node_selection: new Cproperty__type__number_expression.Dnode_selection(init['node selection'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalue__type__number_expression = {
	'value':Tnumber_value;
};
export class Cvalue__type__number_expression extends AlanNode {
	public readonly properties:{
		readonly value:Cnumber_value
	};
	constructor(init:Tvalue__type__number_expression, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cvalue__type__number_expression.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps];
};
export class Cnode_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cnode_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tnode_path, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application,
		existence_support: () => application.Cexistence__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cnode_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps = {
};
export class Cno__has_steps extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps, public parent:Cnode_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps = {
	'tail':Tnode_path;
	'type':['collection entry', Tcollection_entry__type__yes__has_steps]|['group', Tgroup__type__yes__has_steps]|'parent'|['parent', {}]|['reference', Treference]|['reference rule', Treference_rule]|['state', Tstate__type__yes__has_steps]|['state rule', Tstate_rule];
};
export class Cyes__has_steps extends AlanNode {
	public readonly properties:{
		readonly tail:Cnode_path,
		readonly type:Cyes__has_steps.Dtype
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps, public parent:Cnode_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps.Dtail(init['tail'], $this),
			type: new Cyes__has_steps.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry__type__yes__has_steps = {
	'collection':string;
	'entry key':string;
};
export class Ccollection_entry__type__yes__has_steps extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection_entry__type__yes__has_steps.Dcollection,
		readonly entry_key:string
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		optional_existence: () => application.Coptional__existence
	} = {
		optional_existence: cache((detach:boolean) => {
			const query__node_path__has_steps__yes__type__collection_entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tcollection_entry__type__yes__has_steps, public parent:Cyes__has_steps) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection_entry__type__yes__has_steps.Dcollection(init['collection'], $this),
			entry_key: init['entry key']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps = {
	'group':string;
};
export class Cgroup__type__yes__has_steps extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps.Dgroup
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps, public parent:Cyes__has_steps) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparent = {
};
export class Cparent extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent_node()).result!)
	}
	public readonly inferences:{
		parent_node: () => application.Cnode__application
	} = {
		parent_node: cache((detach:boolean) => {
			const query__node_path__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.node())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Tparent, public parent:Cyes__has_steps) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference = {
	'text':string;
};
export class Creference extends AlanNode {
	public readonly properties:{
		readonly text:Creference.Dtext
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.text?.inferences.reference())
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.referenced_node_type()).result!)
	}
	constructor(init:Treference, public parent:Cyes__has_steps) {
		super();
		const $this = this;
		this.properties = {
			text: new Creference.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_rule = {
	'property':string;
	'rule':string;
};
export class Creference_rule extends AlanNode {
	public readonly properties:{
		readonly property:Creference_rule.Dproperty,
		readonly rule:Creference_rule.Drule
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!)
	}
	constructor(init:Treference_rule, public parent:Cyes__has_steps) {
		super();
		const $this = this;
		this.properties = {
			property: new Creference_rule.Dproperty(init['property'], $this),
			rule: new Creference_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps = {
	'state':string;
	'state group':string;
};
export class Cstate__type__yes__has_steps extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps.Dstate,
		readonly state_group:Cstate__type__yes__has_steps.Dstate_group
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		optional_existence: () => application.Coptional__existence
	} = {
		optional_existence: cache((detach:boolean) => {
			const query__node_path__has_steps__yes__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tstate__type__yes__has_steps, public parent:Cyes__has_steps) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps.Dstate(init['state'], $this),
			state_group: new Cstate__type__yes__has_steps.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_rule = {
	'rule':string;
};
export class Cstate_rule extends AlanNode {
	public readonly properties:{
		readonly rule:Cstate_rule.Drule
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!)
	}
	public readonly inferences:{
		state: () => application.Cstates__state_group
	} = {
		state: cache((detach:boolean) => {
			const query__node_path__has_steps__yes__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as application.Cstates__state_group : undefined).result!;
		})

	}
	constructor(init:Tstate_rule, public parent:Cyes__has_steps) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cstate_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tid_path = {
	'has step':'no'|['no', {}]|['yes', Tyes__has_step__id_path];
};
export class Cid_path extends AlanNode {
	public readonly properties:{
		readonly has_step:Cid_path.Dhas_step
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_step?.state.node.output.node()).result!)
	};
	constructor(init:Tid_path, public location:AlanNode, public input: {
		node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_step: new Cid_path.Dhas_step(init['has step'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_step__id_path = {
};
export class Cno__has_step__id_path extends AlanNode {
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno__has_step__id_path, public parent:Cid_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_step__id_path = {
	'tail':Tid_path;
	'type':['collection entry', Tcollection_entry__type__yes__has_step]|['group', Tgroup__type__yes__has_step__id_path]|['state', Tstate__type__yes__has_step__id_path];
};
export class Cyes__has_step__id_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cid_path,
		readonly type:Cyes__has_step__id_path.Dtype
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_step__id_path, public parent:Cid_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_step__id_path.Dtail(init['tail'], $this),
			type: new Cyes__has_step__id_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry__type__yes__has_step = {
	'attribute':string;
	'entry id':Ttext_value;
};
export class Ccollection_entry__type__yes__has_step extends AlanNode {
	public readonly properties:{
		readonly attribute:Ccollection_entry__type__yes__has_step.Dattribute,
		readonly entry_id:Ctext_value
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.attribute?.inferences.collection())
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcollection_entry__type__yes__has_step, public parent:Cyes__has_step__id_path) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Ccollection_entry__type__yes__has_step.Dattribute(init['attribute'], $this),
			entry_id: new Ccollection_entry__type__yes__has_step.Dentry_id(init['entry id'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_step__id_path = {
	'attribute':string;
};
export class Cgroup__type__yes__has_step__id_path extends AlanNode {
	public readonly properties:{
		readonly attribute:Cgroup__type__yes__has_step__id_path.Dattribute
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.attribute?.inferences.group())
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes__has_step__id_path, public parent:Cyes__has_step__id_path) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cgroup__type__yes__has_step__id_path.Dattribute(init['attribute'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_step__id_path = {
	'attribute':string;
	'state':string;
};
export class Cstate__type__yes__has_step__id_path extends AlanNode {
	public readonly properties:{
		readonly attribute:Cstate__type__yes__has_step__id_path.Dattribute,
		readonly state:Cstate__type__yes__has_step__id_path.Dstate
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate__type__yes__has_step__id_path, public parent:Cyes__has_step__id_path) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cstate__type__yes__has_step__id_path.Dattribute(init['attribute'], $this),
			state: new Cstate__type__yes__has_step__id_path.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentity_descendant_selector = {
	'has step':'no'|['no', {}]|['yes', Tyes__has_step__entity_descendant_selector];
};
export class Centity_descendant_selector extends AlanNode {
	public readonly properties:{
		readonly has_step:Centity_descendant_selector.Dhas_step
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_step?.state.node.output.node()).result!)
	};
	constructor(init:Tentity_descendant_selector, public location:AlanNode, public input: {
		node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_step: new Centity_descendant_selector.Dhas_step(init['has step'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_step__entity_descendant_selector = {
};
export class Cno__has_step__entity_descendant_selector extends AlanNode {
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno__has_step__entity_descendant_selector, public parent:Centity_descendant_selector) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_step__entity_descendant_selector = {
	'tail':Tentity_descendant_selector;
	'type':['group', Tgroup__type__yes__has_step__entity_descendant_selector]|['state', Tstate__type__yes__has_step__entity_descendant_selector];
};
export class Cyes__has_step__entity_descendant_selector extends AlanNode {
	public readonly properties:{
		readonly tail:Centity_descendant_selector,
		readonly type:Cyes__has_step__entity_descendant_selector.Dtype
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node()).result!)
	}
	constructor(init:Tyes__has_step__entity_descendant_selector, public parent:Centity_descendant_selector) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_step__entity_descendant_selector.Dtail(init['tail'], $this),
			type: new Cyes__has_step__entity_descendant_selector.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_step__entity_descendant_selector = {
	'attribute':string;
};
export class Cgroup__type__yes__has_step__entity_descendant_selector extends AlanNode {
	public readonly properties:{
		readonly attribute:Cgroup__type__yes__has_step__entity_descendant_selector.Dattribute
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.attribute?.inferences.group())
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes__has_step__entity_descendant_selector, public parent:Cyes__has_step__entity_descendant_selector) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cgroup__type__yes__has_step__entity_descendant_selector.Dattribute(init['attribute'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_step__entity_descendant_selector = {
	'attribute':string;
	'state':string;
};
export class Cstate__type__yes__has_step__entity_descendant_selector extends AlanNode {
	public readonly properties:{
		readonly attribute:Cstate__type__yes__has_step__entity_descendant_selector.Dattribute,
		readonly state:Cstate__type__yes__has_step__entity_descendant_selector.Dstate
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate__type__yes__has_step__entity_descendant_selector, public parent:Cyes__has_step__entity_descendant_selector) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cstate__type__yes__has_step__entity_descendant_selector.Dattribute(init['attribute'], $this),
			state: new Cstate__type__yes__has_step__entity_descendant_selector.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_selector = {
	'has step':'no'|['no', {}]|['yes', Tyes__has_step__collection_selector];
};
export class Ccollection_selector extends AlanNode {
	public readonly properties:{
		readonly has_step:Ccollection_selector.Dhas_step
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_step?.state.node.output.node()).result!)
	};
	constructor(init:Tcollection_selector, public location:AlanNode, public input: {
		node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_step: new Ccollection_selector.Dhas_step(init['has step'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_step__collection_selector = {
};
export class Cno__has_step__collection_selector extends AlanNode {
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno__has_step__collection_selector, public parent:Ccollection_selector) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_step__collection_selector = {
	'attribute':string;
	'head':Tentity_descendant_selector;
	'tail':Tcollection_selector;
};
export class Cyes__has_step__collection_selector extends AlanNode {
	public readonly properties:{
		readonly attribute:Cyes__has_step__collection_selector.Dattribute,
		readonly head:Centity_descendant_selector,
		readonly tail:Ccollection_selector
	};
	public readonly output:{
		node: () => application.Cnode__application;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node()).result!)
	}
	constructor(init:Tyes__has_step__collection_selector, public parent:Ccollection_selector) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cyes__has_step__collection_selector.Dattribute(init['attribute'], $this),
			head: new Cyes__has_step__collection_selector.Dhead(init['head'], $this),
			tail: new Cyes__has_step__collection_selector.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has step'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_format = {
	'type':['join', Tjoin];
};
export class Ccollection_format extends AlanNode {
	public readonly properties:{
		readonly type:Ccollection_format.Dtype
	};
	constructor(init:Tcollection_format, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Ccollection_format.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tjoin = {
	'separator':Ttext_value;
};
export class Cjoin extends AlanNode {
	public readonly properties:{
		readonly separator:Ctext_value
	};
	constructor(init:Tjoin, public parent:Ccollection_format) {
		super();
		const $this = this;
		this.properties = {
			separator: new Cjoin.Dseparator(init['separator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'join'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_expression = {
	'type':['property', Tproperty__type__collection_expression];
};
export class Ccollection_expression extends AlanNode {
	public readonly properties:{
		readonly type:Ccollection_expression.Dtype
	};
	constructor(init:Tcollection_expression, public location:AlanNode, public input: {
		node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Ccollection_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tproperty__type__collection_expression = {
	'attribute':string;
	'node selection':Tnode_path;
	'property':Ttext_expression;
};
export class Cproperty__type__collection_expression extends AlanNode {
	public readonly properties:{
		readonly attribute:Cproperty__type__collection_expression.Dattribute,
		readonly node_selection:Cnode_path,
		readonly property:Ctext_expression
	};
	constructor(init:Tproperty__type__collection_expression, public parent:Ccollection_expression) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cproperty__type__collection_expression.Dattribute(init['attribute'], $this),
			node_selection: new Cproperty__type__collection_expression.Dnode_selection(init['node selection'], $this),
			property: new Cproperty__type__collection_expression.Dproperty(init['property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}

export type Tquery = {
	'query type':['collection', Tcollection__query_type]|['graph', Tgraph];
};
export class Cquery extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public readonly properties:{
		readonly query_type:Cquery.Dquery_type
	};
	constructor(init:Tquery, public readonly input: {
	'application':application.Capplication
	'parameters':parameters.Cparameters}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			query_type: new Cquery.Dquery_type(init['query type'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export type Tcollection__query_type = {
	'collection path':Tcollection_selector;
	'context node path':Tid_path;
	'filters':Tfilters__collection;
	'has first':'no'|['no', {}]|['yes', Tyes__has_first__collection];
	'properties':Record<string, Tproperties__collection>;
	'record limit':Tnumber_value;
};
export class Ccollection__query_type extends AlanNode {
	public readonly properties:{
		readonly collection_path:Ccollection_selector,
		readonly context_node_path:Cid_path,
		readonly filters:Cfilters__collection,
		readonly has_first:Ccollection__query_type.Dhas_first,
		readonly properties:Ccollection__query_type.Dproperties,
		readonly record_limit:Cnumber_value
	};
	constructor(init:Tcollection__query_type, public parent:Cquery) {
		super();
		const $this = this;
		this.properties = {
			collection_path: new Ccollection__query_type.Dcollection_path(init['collection path'], $this),
			context_node_path: new Ccollection__query_type.Dcontext_node_path(init['context node path'], $this),
			filters: new Cfilters__collection(init['filters'], $this),
			has_first: new Ccollection__query_type.Dhas_first(init['has first'], $this),
			properties: new Ccollection__query_type.Dproperties(init['properties'], $this),
			record_limit: new Ccollection__query_type.Drecord_limit(init['record limit'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'query type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfilters__collection = {
	'filters':Tfilters__filters[];
	'todo filter':'no'|['no', {}]|['yes', Tyes__todo_filter];
};
export class Cfilters__collection extends AlanNode {
	public readonly properties:{
		readonly filters:Cfilters__collection.Dfilters,
		readonly todo_filter:Cfilters__collection.Dtodo_filter
	};
	constructor(init:Tfilters__collection, public parent:Ccollection__query_type) {
		super();
		const $this = this;
		this.properties = {
			filters: new Cfilters__collection.Dfilters(init['filters'], $this),
			todo_filter: new Cfilters__collection.Dtodo_filter(init['todo filter'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filters'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfilters__filters = {
	'path':Tnode_path;
	'type':['node', Tnode]|['property', Tproperty__type__filters];
};
export class Cfilters__filters extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path,
		readonly type:Cfilters__filters.Dtype
	};
	constructor(init:Tfilters__filters, public parent:Cfilters__collection) {
		super();
		const $this = this;
		this.properties = {
			path: new Cfilters__filters.Dpath(init['path'], $this),
			type: new Cfilters__filters.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filters'`; }
	public get entity() { return this; }
}
export type Tnode = {
	'type':['containment', Tcontainment]|['existence', Texistence];
};
export class Cnode extends AlanNode {
	public readonly properties:{
		readonly type:Cnode.Dtype
	};
	constructor(init:Tnode, public parent:Cfilters__filters) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnode.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontainment = {
	'operator':'in'|['in', {}]|'not in'|['not in', {}];
	'references':Treference_filter_list;
};
export class Ccontainment extends AlanNode {
	public readonly properties:{
		readonly operator:Ccontainment.Doperator,
		readonly references:Creference_filter_list
	};
	constructor(init:Tcontainment, public parent:Cnode) {
		super();
		const $this = this;
		this.properties = {
			operator: new Ccontainment.Doperator(init['operator'], $this),
			references: new Ccontainment.Dreferences(init['references'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'containment'`; }
	public get entity() { return this.parent.entity; }
}
export type Tin = {
};
export class Cin extends AlanNode {
	constructor(init:Tin, public parent:Ccontainment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'in'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_in = {
};
export class Cnot_in extends AlanNode {
	constructor(init:Tnot_in, public parent:Ccontainment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'not in'`; }
	public get entity() { return this.parent.entity; }
}
export type Texistence = {
	'operator':'exists'|['exists', {}]|'not exists'|['not exists', {}];
};
export class Cexistence extends AlanNode {
	public readonly properties:{
		readonly operator:Cexistence.Doperator
	};
	constructor(init:Texistence, public parent:Cnode) {
		super();
		const $this = this;
		this.properties = {
			operator: new Cexistence.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'existence'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists = {
};
export class Cexists extends AlanNode {
	constructor(init:Texists, public parent:Cexistence) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_exists = {
};
export class Cnot_exists extends AlanNode {
	constructor(init:Tnot_exists, public parent:Cexistence) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'not exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__type__filters = {
	'property':string;
	'type':['number', Tnumber__type__property]|['state group', Tstate_group];
};
export class Cproperty__type__filters extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty__type__filters.Dproperty,
		readonly type:Cproperty__type__filters.Dtype
	};
	constructor(init:Tproperty__type__filters, public parent:Cfilters__filters) {
		super();
		const $this = this;
		this.properties = {
			property: new Cproperty__type__filters.Dproperty(init['property'], $this),
			type: new Cproperty__type__filters.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property = {
	'expression':Tnumber_filter_expression;
};
export class Cnumber__type__property extends AlanNode {
	public readonly properties:{
		readonly expression:Cnumber_filter_expression
	};
	public readonly inferences:{
		number_type: () => application.Cnumber__type__property
	} = {
		number_type: cache((detach:boolean) => {
			const query__query_type__collection__filters__filters__type__property__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__property, public parent:Cproperty__type__filters) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnumber__type__property.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group = {
	'states to include':Record<string, {}>;
};
export class Cstate_group extends AlanNode {
	public readonly properties:{
		readonly states_to_include:Cstate_group.Dstates_to_include
	};
	public readonly inferences:{
		state_group_type: () => application.Cstate_group
	} = {
		state_group_type: cache((detach:boolean) => {
			const query__query_type__collection__filters__filters__type__property__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group, public parent:Cproperty__type__filters) {
		super();
		const $this = this;
		this.properties = {
			states_to_include: new Cstate_group.Dstates_to_include(init['states to include'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates_to_include extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates_to_include) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group_type())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states to include'::key`; }
}
export type Tstates_to_include = {
};
export class Cstates_to_include extends AlanDictionaryEntry {
	public key:Kstates_to_include;
	public get key_value() { return this.key.entry; }
	constructor(key:string, init:Tstates_to_include, public parent:Cstate_group) {
		super();
		const $this = this;
		this.key = new Kstates_to_include(key, $this);
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states to include'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__todo_filter = {
};
export class Cno__todo_filter extends AlanNode {
	constructor(init:Tno__todo_filter, public parent:Cfilters__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__todo_filter = {
	'path':Tnode_path;
};
export class Cyes__todo_filter extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path & { readonly inferences: {
			has_todo: () => application.Cyes__todo;
		} }
	};
	constructor(init:Tyes__todo_filter, public parent:Cfilters__collection) {
		super();
		const $this = this;
		this.properties = {
			path: new Cyes__todo_filter.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_first__collection = {
};
export class Cno__has_first__collection extends AlanNode {
	constructor(init:Tno__has_first__collection, public parent:Ccollection__query_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has first'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_first__collection = {
	'first':string;
};
export class Cyes__has_first__collection extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_first__collection.Dfirst
	};
	constructor(init:Tyes__has_first__collection, public parent:Ccollection__query_type) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_first__collection.Dfirst(init['first'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has first'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperties__collection = {
	'has next':'no'|['no', {}]|['yes', Tyes__has_next__properties__collection];
	'type':['collection', Tcollection__type]|['number', Tnumber__type__properties__collection]|['stategroup', Tstategroup__type__properties__collection]|['text', Ttext__type__properties__collection];
};
export class Cproperties__collection extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_next:Cproperties__collection.Dhas_next,
		readonly type:Cproperties__collection.Dtype
	};
	constructor(key:string, init:Tproperties__collection, public parent:Ccollection__query_type) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_next: new Cproperties__collection.Dhas_next(init['has next'], $this),
			type: new Cproperties__collection.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_next__properties__collection = {
};
export class Cno__has_next__properties__collection extends AlanNode {
	constructor(init:Tno__has_next__properties__collection, public parent:Cproperties__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next__properties__collection = {
	'next':string;
};
export class Cyes__has_next__properties__collection extends AlanNode {
	public readonly properties:{
		readonly next:Cyes__has_next__properties__collection.Dnext
	};
	constructor(init:Tyes__has_next__properties__collection, public parent:Cproperties__collection) {
		super();
		const $this = this;
		this.properties = {
			next: new Cyes__has_next__properties__collection.Dnext(init['next'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type = {
	'format':Tcollection_format;
	'value':Tcollection_expression;
};
export class Ccollection__type extends AlanNode {
	public readonly properties:{
		readonly format:Ccollection_format,
		readonly value:Ccollection_expression
	};
	constructor(init:Tcollection__type, public parent:Cproperties__collection) {
		super();
		const $this = this;
		this.properties = {
			format: new Ccollection__type.Dformat(init['format'], $this),
			value: new Ccollection__type.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__properties__collection = {
	'format':Tnumber_format;
	'value':Tnumber_expression;
};
export class Cnumber__type__properties__collection extends AlanNode {
	public readonly properties:{
		readonly format:Cnumber_format,
		readonly value:Cnumber_expression
	};
	constructor(init:Tnumber__type__properties__collection, public parent:Cproperties__collection) {
		super();
		const $this = this;
		this.properties = {
			format: new Cnumber__type__properties__collection.Dformat(init['format'], $this),
			value: new Cnumber__type__properties__collection.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstategroup__type__properties__collection = {
	'format':Tstate_format;
	'value':Tstategroup_expression;
};
export class Cstategroup__type__properties__collection extends AlanNode {
	public readonly properties:{
		readonly format:Cstate_format,
		readonly value:Cstategroup_expression
	};
	constructor(init:Tstategroup__type__properties__collection, public parent:Cproperties__collection) {
		super();
		const $this = this;
		this.properties = {
			format: new Cstategroup__type__properties__collection.Dformat(init['format'], $this),
			value: new Cstategroup__type__properties__collection.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'stategroup'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__properties__collection = {
	'format':Ttext_format;
	'value':Ttext_expression;
};
export class Ctext__type__properties__collection extends AlanNode {
	public readonly properties:{
		readonly format:Ctext_format,
		readonly value:Ctext_expression
	};
	constructor(init:Ttext__type__properties__collection, public parent:Cproperties__collection) {
		super();
		const $this = this;
		this.properties = {
			format: new Ctext__type__properties__collection.Dformat(init['format'], $this),
			value: new Ctext__type__properties__collection.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgraph = {
	'context selection':Tid_path;
	'entry points':Tentry_points;
	'query':Tquery__;
};
export class Cgraph extends AlanNode {
	public readonly properties:{
		readonly context_selection:Cid_path,
		readonly entry_points:Centry_points,
		readonly query:Cquery__
	};
	constructor(init:Tgraph, public parent:Cquery) {
		super();
		const $this = this;
		this.properties = {
			context_selection: new Cgraph.Dcontext_selection(init['context selection'], $this),
			entry_points: new Centry_points(init['entry points'], $this),
			query: new Cquery__(init['query'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'query type'?'graph'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry_points = {
	'attribute':string;
	'collection selection':Tcollection_selector;
	'graph':string;
};
export class Centry_points extends AlanNode {
	public readonly properties:{
		readonly attribute:Centry_points.Dattribute,
		readonly collection_selection:Ccollection_selector,
		readonly graph:Centry_points.Dgraph
	};
	constructor(init:Tentry_points, public parent:Cgraph) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Centry_points.Dattribute(init['attribute'], $this),
			collection_selection: new Centry_points.Dcollection_selection(init['collection selection'], $this),
			graph: new Centry_points.Dgraph(init['graph'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entry points'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__ = {
	'collection selection':Tcollection_selector;
	'has first':'no'|['no', {}]|['yes', Tyes__has_first__query];
	'properties':Record<string, Tproperties__query>;
};
export class Cquery__ extends AlanNode {
	public readonly properties:{
		readonly collection_selection:Ccollection_selector,
		readonly has_first:Cquery__.Dhas_first,
		readonly properties:Cquery__.Dproperties
	};
	constructor(init:Tquery__, public parent:Cgraph) {
		super();
		const $this = this;
		this.properties = {
			collection_selection: new Cquery__.Dcollection_selection(init['collection selection'], $this),
			has_first: new Cquery__.Dhas_first(init['has first'], $this),
			properties: new Cquery__.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_first__query = {
};
export class Cno__has_first__query extends AlanNode {
	constructor(init:Tno__has_first__query, public parent:Cquery__) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has first'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_first__query = {
	'first':string;
};
export class Cyes__has_first__query extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_first__query.Dfirst
	};
	constructor(init:Tyes__has_first__query, public parent:Cquery__) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_first__query.Dfirst(init['first'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has first'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperties__query = {
	'has next':'no'|['no', {}]|['yes', Tyes__has_next__properties__query];
	'path':Tnode_path;
	'type':['number', Tnumber__type__properties__query]|['stategroup', Tstategroup__type__properties__query]|['text', Ttext__type__properties__query];
};
export class Cproperties__query extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_next:Cproperties__query.Dhas_next,
		readonly path:Cnode_path,
		readonly type:Cproperties__query.Dtype
	};
	constructor(key:string, init:Tproperties__query, public parent:Cquery__) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_next: new Cproperties__query.Dhas_next(init['has next'], $this),
			path: new Cproperties__query.Dpath(init['path'], $this),
			type: new Cproperties__query.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_next__properties__query = {
};
export class Cno__has_next__properties__query extends AlanNode {
	constructor(init:Tno__has_next__properties__query, public parent:Cproperties__query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next__properties__query = {
	'next':string;
};
export class Cyes__has_next__properties__query extends AlanNode {
	public readonly properties:{
		readonly next:Cyes__has_next__properties__query.Dnext
	};
	constructor(init:Tyes__has_next__properties__query, public parent:Cproperties__query) {
		super();
		const $this = this;
		this.properties = {
			next: new Cyes__has_next__properties__query.Dnext(init['next'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__properties__query = {
	'format':Tnumber_format;
	'number':string;
};
export class Cnumber__type__properties__query extends AlanNode {
	public readonly properties:{
		readonly format:Cnumber_format,
		readonly number:Cnumber__type__properties__query.Dnumber
	};
	constructor(init:Tnumber__type__properties__query, public parent:Cproperties__query) {
		super();
		const $this = this;
		this.properties = {
			format: new Cnumber__type__properties__query.Dformat(init['format'], $this),
			number: new Cnumber__type__properties__query.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstategroup__type__properties__query = {
	'format':Tstate_format;
	'state group':string;
};
export class Cstategroup__type__properties__query extends AlanNode {
	public readonly properties:{
		readonly format:Cstate_format,
		readonly state_group:Cstategroup__type__properties__query.Dstate_group
	};
	constructor(init:Tstategroup__type__properties__query, public parent:Cproperties__query) {
		super();
		const $this = this;
		this.properties = {
			format: new Cstategroup__type__properties__query.Dformat(init['format'], $this),
			state_group: new Cstategroup__type__properties__query.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'stategroup'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__properties__query = {
	'format':Ttext_format;
	'text':string;
};
export class Ctext__type__properties__query extends AlanNode {
	public readonly properties:{
		readonly format:Ctext_format,
		readonly text:Ctext__type__properties__query.Dtext
	};
	constructor(init:Ttext__type__properties__query, public parent:Cproperties__query) {
		super();
		const $this = this;
		this.properties = {
			format: new Ctext__type__properties__query.Dformat(init['format'], $this),
			text: new Ctext__type__properties__query.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Ccollection_expression {
	export class Dtype extends StateGroup<
		{ name: 'property', node:Cproperty__type__collection_expression, init:Tproperty__type__collection_expression}> {
		protected initializer(state: 'property') {
			switch (state) {
				case 'property': return (init:Tproperty__type__collection_expression, parent:Ccollection_expression) => new Cproperty__type__collection_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'property') {
			switch (state) {
				case 'property': return finalize_property__type__collection_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_expression['type'], parent:Ccollection_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cproperty__type__collection_expression {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			collection: () => application.Ccollection__type__property
		}

		constructor(data:string, $this:Cproperty__type__collection_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.node_selection)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				collection: cache((detach:boolean) => {
					const query__collection_expression__type__property__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__collection_expression__type__property__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'collection') {
										return context.properties.type.state.node as application.Ccollection__type__property;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dnode_selection extends Cnode_path {
		constructor(data:Tproperty__type__collection_expression['node selection'], parent:Cproperty__type__collection_expression) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'node selection')
		}
	}
	export class Dproperty extends Ctext_expression {
		constructor(data:Tproperty__type__collection_expression['property'], parent:Cproperty__type__collection_expression) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.attribute?.inferences.collection())
					.then(context => context?.properties.node).result!)
			}, 'property')
		}
	}
}
export namespace Ccollection_format {
	export class Dtype extends StateGroup<
		{ name: 'join', node:Cjoin, init:Tjoin}> {
		protected initializer(state: 'join') {
			switch (state) {
				case 'join': return (init:Tjoin, parent:Ccollection_format) => new Cjoin(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'join') {
			switch (state) {
				case 'join': return finalize_join;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_format['type'], parent:Ccollection_format) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cjoin {
	export class Dseparator extends Ctext_value {
		constructor(data:Tjoin['separator'], parent:Cjoin) {
			super(data, parent, 'separator')
		}
	}
}
export namespace Ccollection_selector {
	export class Dhas_step extends StateGroup<
		{ name: 'no', node:Cno__has_step__collection_selector, init:Tno__has_step__collection_selector}|
		{ name: 'yes', node:Cyes__has_step__collection_selector, init:Tyes__has_step__collection_selector}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_step__collection_selector, parent:Ccollection_selector) => new Cno__has_step__collection_selector(init, parent);
				case 'yes': return (init:Tyes__has_step__collection_selector, parent:Ccollection_selector) => new Cyes__has_step__collection_selector(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_step__collection_selector;
				case 'yes': return finalize_yes__has_step__collection_selector;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_selector['has step'], parent:Ccollection_selector) {
			super(data, parent, 'has step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has step'`; }
	}
}
export namespace Cyes__has_step__collection_selector {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			collection: () => application.Ccollection__type__property
		}

		constructor(data:string, $this:Cyes__has_step__collection_selector) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				collection: cache((detach:boolean) => {
					const query__collection_selector__has_step__yes__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__collection_selector__has_step__yes__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'collection') {
										return context.properties.type.state.node as application.Ccollection__type__property;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dhead extends Centity_descendant_selector {
		constructor(data:Tyes__has_step__collection_selector['head'], parent:Cyes__has_step__collection_selector) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!)
			}, 'head')
		}
	}
	export class Dtail extends Ccollection_selector {
		constructor(data:Tyes__has_step__collection_selector['tail'], parent:Cyes__has_step__collection_selector) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.attribute?.inferences.collection())
					.then(context => context?.properties.node).result!)
			}, 'tail')
		}
	}
}
export namespace Centity_descendant_selector {
	export class Dhas_step extends StateGroup<
		{ name: 'no', node:Cno__has_step__entity_descendant_selector, init:Tno__has_step__entity_descendant_selector}|
		{ name: 'yes', node:Cyes__has_step__entity_descendant_selector, init:Tyes__has_step__entity_descendant_selector}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_step__entity_descendant_selector, parent:Centity_descendant_selector) => new Cno__has_step__entity_descendant_selector(init, parent);
				case 'yes': return (init:Tyes__has_step__entity_descendant_selector, parent:Centity_descendant_selector) => new Cyes__has_step__entity_descendant_selector(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_step__entity_descendant_selector;
				case 'yes': return finalize_yes__has_step__entity_descendant_selector;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentity_descendant_selector['has step'], parent:Centity_descendant_selector) {
			super(data, parent, 'has step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has step'`; }
	}
}
export namespace Cyes__has_step__entity_descendant_selector {
	export class Dtail extends Centity_descendant_selector {
		constructor(data:Tyes__has_step__entity_descendant_selector['tail'], parent:Cyes__has_step__entity_descendant_selector) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'group', node:Cgroup__type__yes__has_step__entity_descendant_selector, init:Tgroup__type__yes__has_step__entity_descendant_selector}|
		{ name: 'state', node:Cstate__type__yes__has_step__entity_descendant_selector, init:Tstate__type__yes__has_step__entity_descendant_selector}> {
		protected initializer(state: 'group'|'state') {
			switch (state) {
				case 'group': return (init:Tgroup__type__yes__has_step__entity_descendant_selector, parent:Cyes__has_step__entity_descendant_selector) => new Cgroup__type__yes__has_step__entity_descendant_selector(init, parent);
				case 'state': return (init:Tstate__type__yes__has_step__entity_descendant_selector, parent:Cyes__has_step__entity_descendant_selector) => new Cstate__type__yes__has_step__entity_descendant_selector(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'group'|'state') {
			switch (state) {
				case 'group': return finalize_group__type__yes__has_step__entity_descendant_selector;
				case 'state': return finalize_state__type__yes__has_step__entity_descendant_selector;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_step__entity_descendant_selector['type'], parent:Cyes__has_step__entity_descendant_selector) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cgroup__type__yes__has_step__entity_descendant_selector {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			group: () => application.Cgroup__type__property
		}

		constructor(data:string, $this:Cgroup__type__yes__has_step__entity_descendant_selector) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				group: cache((detach:boolean) => {
					const query__entity_descendant_selector__has_step__yes__type__group__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__entity_descendant_selector__has_step__yes__type__group__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'group') {
										return context.properties.type.state.node as application.Cgroup__type__property;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
}
export namespace Cstate__type__yes__has_step__entity_descendant_selector {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			stategroup: () => application.Cstate_group
		}

		constructor(data:string, $this:Cstate__type__yes__has_step__entity_descendant_selector) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				stategroup: cache((detach:boolean) => {
					const query__entity_descendant_selector__has_step__yes__type__state__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__entity_descendant_selector__has_step__yes__type__state__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'state group') {
										return context.properties.type.state.node as application.Cstate_group;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_step__entity_descendant_selector) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.attribute?.inferences.stategroup())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cid_path {
	export class Dhas_step extends StateGroup<
		{ name: 'no', node:Cno__has_step__id_path, init:Tno__has_step__id_path}|
		{ name: 'yes', node:Cyes__has_step__id_path, init:Tyes__has_step__id_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_step__id_path, parent:Cid_path) => new Cno__has_step__id_path(init, parent);
				case 'yes': return (init:Tyes__has_step__id_path, parent:Cid_path) => new Cyes__has_step__id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_step__id_path;
				case 'yes': return finalize_yes__has_step__id_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tid_path['has step'], parent:Cid_path) {
			super(data, parent, 'has step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has step'`; }
	}
}
export namespace Cyes__has_step__id_path {
	export class Dtail extends Cid_path {
		constructor(data:Tyes__has_step__id_path['tail'], parent:Cyes__has_step__id_path) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection entry', node:Ccollection_entry__type__yes__has_step, init:Tcollection_entry__type__yes__has_step}|
		{ name: 'group', node:Cgroup__type__yes__has_step__id_path, init:Tgroup__type__yes__has_step__id_path}|
		{ name: 'state', node:Cstate__type__yes__has_step__id_path, init:Tstate__type__yes__has_step__id_path}> {
		protected initializer(state: 'collection entry'|'group'|'state') {
			switch (state) {
				case 'collection entry': return (init:Tcollection_entry__type__yes__has_step, parent:Cyes__has_step__id_path) => new Ccollection_entry__type__yes__has_step(init, parent);
				case 'group': return (init:Tgroup__type__yes__has_step__id_path, parent:Cyes__has_step__id_path) => new Cgroup__type__yes__has_step__id_path(init, parent);
				case 'state': return (init:Tstate__type__yes__has_step__id_path, parent:Cyes__has_step__id_path) => new Cstate__type__yes__has_step__id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection entry'|'group'|'state') {
			switch (state) {
				case 'collection entry': return finalize_collection_entry__type__yes__has_step;
				case 'group': return finalize_group__type__yes__has_step__id_path;
				case 'state': return finalize_state__type__yes__has_step__id_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_step__id_path['type'], parent:Cyes__has_step__id_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection_entry__type__yes__has_step {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			collection: () => application.Ccollection__type__property
		}

		constructor(data:string, $this:Ccollection_entry__type__yes__has_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				collection: cache((detach:boolean) => {
					const query__id_path__has_step__yes__type__collection_entry__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__id_path__has_step__yes__type__collection_entry__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'collection') {
										return context.properties.type.state.node as application.Ccollection__type__property;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dentry_id extends Ctext_value {
		constructor(data:Tcollection_entry__type__yes__has_step['entry id'], parent:Ccollection_entry__type__yes__has_step) {
			super(data, parent, 'entry id')
		}
	}
}
export namespace Cgroup__type__yes__has_step__id_path {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			group: () => application.Cgroup__type__property
		}

		constructor(data:string, $this:Cgroup__type__yes__has_step__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				group: cache((detach:boolean) => {
					const query__id_path__has_step__yes__type__group__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__id_path__has_step__yes__type__group__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'group') {
										return context.properties.type.state.node as application.Cgroup__type__property;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
}
export namespace Cstate__type__yes__has_step__id_path {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			stategroup: () => application.Cstate_group
		}

		constructor(data:string, $this:Cstate__type__yes__has_step__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				stategroup: cache((detach:boolean) => {
					const query__id_path__has_step__yes__type__state__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__id_path__has_step__yes__type__state__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'state group') {
										return context.properties.type.state.node as application.Cstate_group;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_step__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.attribute?.inferences.stategroup())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cnode_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps, init:Tno__has_steps}|
		{ name: 'yes', node:Cyes__has_steps, init:Tyes__has_steps}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps, parent:Cnode_path) => new Cno__has_steps(init, parent);
				case 'yes': return (init:Tyes__has_steps, parent:Cnode_path) => new Cyes__has_steps(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps;
				case 'yes': return finalize_yes__has_steps;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_path['has steps'], parent:Cnode_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps {
	export class Dtail extends Cnode_path {
		constructor(data:Tyes__has_steps['tail'], parent:Cyes__has_steps) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection entry', node:Ccollection_entry__type__yes__has_steps, init:Tcollection_entry__type__yes__has_steps}|
		{ name: 'group', node:Cgroup__type__yes__has_steps, init:Tgroup__type__yes__has_steps}|
		{ name: 'parent', node:Cparent, init:Tparent}|
		{ name: 'reference', node:Creference, init:Treference}|
		{ name: 'reference rule', node:Creference_rule, init:Treference_rule}|
		{ name: 'state', node:Cstate__type__yes__has_steps, init:Tstate__type__yes__has_steps}|
		{ name: 'state rule', node:Cstate_rule, init:Tstate_rule}> {
		protected initializer(state: 'collection entry'|'group'|'parent'|'reference'|'reference rule'|'state'|'state rule') {
			switch (state) {
				case 'collection entry': return (init:Tcollection_entry__type__yes__has_steps, parent:Cyes__has_steps) => new Ccollection_entry__type__yes__has_steps(init, parent);
				case 'group': return (init:Tgroup__type__yes__has_steps, parent:Cyes__has_steps) => new Cgroup__type__yes__has_steps(init, parent);
				case 'parent': return (init:Tparent, parent:Cyes__has_steps) => new Cparent(init, parent);
				case 'reference': return (init:Treference, parent:Cyes__has_steps) => new Creference(init, parent);
				case 'reference rule': return (init:Treference_rule, parent:Cyes__has_steps) => new Creference_rule(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps, parent:Cyes__has_steps) => new Cstate__type__yes__has_steps(init, parent);
				case 'state rule': return (init:Tstate_rule, parent:Cyes__has_steps) => new Cstate_rule(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection entry'|'group'|'parent'|'reference'|'reference rule'|'state'|'state rule') {
			switch (state) {
				case 'collection entry': return finalize_collection_entry__type__yes__has_steps;
				case 'group': return finalize_group__type__yes__has_steps;
				case 'parent': return finalize_parent;
				case 'reference': return finalize_reference;
				case 'reference rule': return finalize_reference_rule;
				case 'state': return finalize_state__type__yes__has_steps;
				case 'state rule': return finalize_state_rule;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps['type'], parent:Cyes__has_steps) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection_entry__type__yes__has_steps {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection_entry__type__yes__has_steps) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cgroup__type__yes__has_steps {
	export class Dgroup extends Reference<application.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as application.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Creference {
	export class Dtext extends Reference<application.Ctext__type__property,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application,
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Creference) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const query__node_path__has_steps__yes__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.text.inferences.reference())
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				reference: cache((detach:boolean) => {
					const query__node_path__has_steps__yes__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => query__node_path__has_steps__yes__type__reference__text_nval)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Creference_rule {
	export class Dproperty extends Reference<application.Ctext__type__property,string> {
		public readonly inferences:{
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Creference_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				reference: cache((detach:boolean) => {
					const query__node_path__has_steps__yes__type__reference_rule__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => query__node_path__has_steps__yes__type__reference_rule__property_nval)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application
		}

		constructor(data:string, $this:Creference_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property?.inferences.reference())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const query__node_path__has_steps__yes__type__reference_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => query__node_path__has_steps__yes__type__reference_rule__rule_nval)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cstate__type__yes__has_steps {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cstate_rule {
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application,
			mandatory_reference: () => application.Cmandatory__type
		}

		constructor(data:string, $this:Cstate_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const query__node_path__has_steps__yes__type__state_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => query__node_path__has_steps__yes__type__state_rule__rule_nval)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				mandatory_reference: cache((detach:boolean) => {
					const query__node_path__has_steps__yes__type__state_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => query__node_path__has_steps__yes__type__state_rule__rule_nval)
						.then(context => context?.properties.behaviour)
						.then(context => {
							if (context?.properties.type.state.name === 'mandatory') {
								return resolve(context.properties.type.state.node as application.Cmandatory__type).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cnumber_expression {
	export class Dtype extends StateGroup<
		{ name: 'property', node:Cproperty__type__number_expression, init:Tproperty__type__number_expression}|
		{ name: 'value', node:Cvalue__type__number_expression, init:Tvalue__type__number_expression}> {
		protected initializer(state: 'property'|'value') {
			switch (state) {
				case 'property': return (init:Tproperty__type__number_expression, parent:Cnumber_expression) => new Cproperty__type__number_expression(init, parent);
				case 'value': return (init:Tvalue__type__number_expression, parent:Cnumber_expression) => new Cvalue__type__number_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'property'|'value') {
			switch (state) {
				case 'property': return finalize_property__type__number_expression;
				case 'value': return finalize_value__type__number_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_expression['type'], parent:Cnumber_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cproperty__type__number_expression {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			number: () => application.Cnumber__type__property
		}

		constructor(data:string, $this:Cproperty__type__number_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.node_selection)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				number: cache((detach:boolean) => {
					const query__number_expression__type__property__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__number_expression__type__property__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'number') {
										return context.properties.type.state.node as application.Cnumber__type__property;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dnode_selection extends Cnode_path {
		constructor(data:Tproperty__type__number_expression['node selection'], parent:Cproperty__type__number_expression) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'node selection')
		}
	}
}
export namespace Cvalue__type__number_expression {
	export class Dvalue extends Cnumber_value {
		constructor(data:Tvalue__type__number_expression['value'], parent:Cvalue__type__number_expression) {
			super(data, parent, 'value')
		}
	}
}
export namespace Cnumber_filter_expression {
	export class Doperator extends StateGroup<
		{ name: 'equals', node:Cequals, init:Tequals}|
		{ name: 'range', node:Crange, init:Trange}> {
		protected initializer(state: 'equals'|'range') {
			switch (state) {
				case 'equals': return (init:Tequals, parent:Cnumber_filter_expression) => new Cequals(init, parent);
				case 'range': return (init:Trange, parent:Cnumber_filter_expression) => new Crange(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'equals'|'range') {
			switch (state) {
				case 'equals': return finalize_equals;
				case 'range': return finalize_range;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_filter_expression['operator'], parent:Cnumber_filter_expression) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cequals {
	export class Dcriterium extends Cnumber_value {
		constructor(data:Tequals['criterium'], parent:Cequals) {
			super(data, parent, 'criterium')
		}
	}
}
export namespace Crange {
	export class Dgreater_than extends StateGroup<
		{ name: 'no', node:Cno__greater_than, init:Tno__greater_than}|
		{ name: 'yes', node:Cyes__greater_than, init:Tyes__greater_than}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__greater_than, parent:Crange) => new Cno__greater_than(init, parent);
				case 'yes': return (init:Tyes__greater_than, parent:Crange) => new Cyes__greater_than(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__greater_than;
				case 'yes': return finalize_yes__greater_than;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trange['greater than'], parent:Crange) {
			super(data, parent, 'greater than');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'greater than'`; }
	}
	export class Dless_than extends StateGroup<
		{ name: 'no', node:Cno__less_than, init:Tno__less_than}|
		{ name: 'yes', node:Cyes__less_than, init:Tyes__less_than}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__less_than, parent:Crange) => new Cno__less_than(init, parent);
				case 'yes': return (init:Tyes__less_than, parent:Crange) => new Cyes__less_than(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__less_than;
				case 'yes': return finalize_yes__less_than;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trange['less than'], parent:Crange) {
			super(data, parent, 'less than');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'less than'`; }
	}
}
export namespace Cyes__greater_than {
	export class Dcriterium extends Cnumber_value {
		constructor(data:Tyes__greater_than['criterium'], parent:Cyes__greater_than) {
			super(data, parent, 'criterium')
		}
	}
}
export namespace Cyes__less_than {
	export class Dcriterium extends Cnumber_value {
		constructor(data:Tyes__less_than['criterium'], parent:Cyes__less_than) {
			super(data, parent, 'criterium')
		}
	}
}
export namespace Cnumber_format {
	export class Dtype extends StateGroup<
		{ name: 'date', node:Cdate, init:Tdate}|
		{ name: 'datetime', node:Cdatetime, init:Tdatetime}|
		{ name: 'decimal', node:Cdecimal, init:Tdecimal}|
		{ name: 'number', node:Cnumber__type__number_format, init:Tnumber__type__number_format}|
		{ name: 'time', node:Ctime, init:Ttime}> {
		protected initializer(state: 'date'|'datetime'|'decimal'|'number'|'time') {
			switch (state) {
				case 'date': return (init:Tdate, parent:Cnumber_format) => new Cdate(init, parent);
				case 'datetime': return (init:Tdatetime, parent:Cnumber_format) => new Cdatetime(init, parent);
				case 'decimal': return (init:Tdecimal, parent:Cnumber_format) => new Cdecimal(init, parent);
				case 'number': return (init:Tnumber__type__number_format, parent:Cnumber_format) => new Cnumber__type__number_format(init, parent);
				case 'time': return (init:Ttime, parent:Cnumber_format) => new Ctime(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'date'|'datetime'|'decimal'|'number'|'time') {
			switch (state) {
				case 'date': return finalize_date;
				case 'datetime': return finalize_datetime;
				case 'decimal': return finalize_decimal;
				case 'number': return finalize_number__type__number_format;
				case 'time': return finalize_time;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_format['type'], parent:Cnumber_format) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdecimal {
	export class Dshift extends AlanInteger {
		constructor(data:Tdecimal['shift'], parent:Cdecimal) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'shift'`; }
	}
}
export namespace Cnumber_value {
	export class Dtype extends StateGroup<
		{ name: 'dynamic', node:Cdynamic__type__number_value, init:Tdynamic__type__number_value}|
		{ name: 'static', node:Cstatic__type__number_value, init:Tstatic__type__number_value}> {
		protected initializer(state: 'dynamic'|'static') {
			switch (state) {
				case 'dynamic': return (init:Tdynamic__type__number_value, parent:Cnumber_value) => new Cdynamic__type__number_value(init, parent);
				case 'static': return (init:Tstatic__type__number_value, parent:Cnumber_value) => new Cstatic__type__number_value(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic'|'static') {
			switch (state) {
				case 'dynamic': return finalize_dynamic__type__number_value;
				case 'static': return finalize_static__type__number_value;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_value['type'], parent:Cnumber_value) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdynamic__type__number_value {
	export class Dparameter extends Reference<parameters.Cparameters__,string> {
		public readonly inferences:{
			number: () => parameters.Cnumber
		}

		constructor(data:string, $this:Cdynamic__type__number_value) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.parameters)
				.then(context => {
					const entry = context?.properties.parameters.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				number: cache((detach:boolean) => {
					const query__number_value__type__dynamic__parameter_nval = $this.properties.parameter.ref;
					return resolve($this.properties.parameter.ref)
						.then(context => query__number_value__type__dynamic__parameter_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'number') {
								return resolve(context.properties.type.state.node as parameters.Cnumber).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'parameter'`; }
	}
}
export namespace Cstatic__type__number_value {
	export class Dvalue extends AlanInteger {
		constructor(data:Tstatic__type__number_value['value'], parent:Cstatic__type__number_value) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Creference_filter_list {
	export class Dcontext extends StateGroup<
		{ name: 'query context', node:Cquery_context, init:Tquery_context}|
		{ name: 'root', node:Croot, init:Troot}> {
		protected initializer(state: 'query context'|'root') {
			switch (state) {
				case 'query context': return (init:Tquery_context, parent:Creference_filter_list) => new Cquery_context(init, parent);
				case 'root': return (init:Troot, parent:Creference_filter_list) => new Croot(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'query context'|'root') {
			switch (state) {
				case 'query context': return finalize_query_context;
				case 'root': return finalize_root;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference_filter_list['context'], parent:Creference_filter_list) {
			super(data, parent, 'context');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'context'`; }
	}
	export class Dhas_alternative extends StateGroup<
		{ name: 'no', node:Cno__has_alternative, init:Tno__has_alternative}|
		{ name: 'yes', node:Cyes__has_alternative, init:Tyes__has_alternative}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_alternative, parent:Creference_filter_list) => new Cno__has_alternative(init, parent);
				case 'yes': return (init:Tyes__has_alternative, parent:Creference_filter_list) => new Cyes__has_alternative(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_alternative;
				case 'yes': return finalize_yes__has_alternative;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference_filter_list['has alternative'], parent:Creference_filter_list) {
			super(data, parent, 'has alternative');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has alternative'`; }
	}
	export class Dreferenced_node extends Cid_path {
		public readonly inferences:{
			entity_constraint: () => application.Centity__application
		}
		constructor(data:Treference_filter_list['referenced node'], parent:Creference_filter_list) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context)
					.then(context => {
						const state_group = context;
						switch (state_group?.state.name) {
							case 'query context': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.root.input.parameters)
									.then(context => context?.properties.context_type)
									.then(context => context?.component_root.output.node()).result;
							}
							case 'root': {
								return resolve(context)
									.then(() => parent)
									.then(context => context?.root.input.application)
									.then(context => context?.properties.root).result;
							}
							case undefined: return undefined;
							default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
						};}).result!)
			}, 'referenced node')
			this.inferences = {
				entity_constraint: cache((detach:boolean) => {
					const query__reference_filter_list__referenced_node_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => query__reference_filter_list__referenced_node_nval)
								.then(context => context?.component_root.output.result_node())
								.then(context => context?.component_root.output.entity()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.entity_node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cyes__has_alternative {
	export class Dalternative extends Creference_filter_list {
		constructor(data:Tyes__has_alternative['alternative'], parent:Cyes__has_alternative) {
			super(data, parent, {
				entity_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity_node()).result!)
			}, 'alternative')
		}
	}
}
export namespace Cstate_format {
	export class Dtype extends StateGroup<
		{ name: 'text', node:Ctext__type__state_format, init:Ttext__type__state_format}> {
		protected initializer(state: 'text') {
			switch (state) {
				case 'text': return (init:Ttext__type__state_format, parent:Cstate_format) => new Ctext__type__state_format(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'text') {
			switch (state) {
				case 'text': return finalize_text__type__state_format;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_format['type'], parent:Cstate_format) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstategroup_expression {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			stategroup: () => application.Cstate_group
		}

		constructor(data:string, $this:Cstategroup_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.node_selection)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				stategroup: cache((detach:boolean) => {
					const query__stategroup_expression__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__stategroup_expression__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'state group') {
										return context.properties.type.state.node as application.Cstate_group;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dnode_selection extends Cnode_path {
		constructor(data:Tstategroup_expression['node selection'], parent:Cstategroup_expression) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'node selection')
		}
	}
}
export namespace Ctext_expression {
	export class Dtype extends StateGroup<
		{ name: 'property', node:Cproperty__type__text_expression, init:Tproperty__type__text_expression}|
		{ name: 'value', node:Cvalue__type__text_expression, init:Tvalue__type__text_expression}> {
		protected initializer(state: 'property'|'value') {
			switch (state) {
				case 'property': return (init:Tproperty__type__text_expression, parent:Ctext_expression) => new Cproperty__type__text_expression(init, parent);
				case 'value': return (init:Tvalue__type__text_expression, parent:Ctext_expression) => new Cvalue__type__text_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'property'|'value') {
			switch (state) {
				case 'property': return finalize_property__type__text_expression;
				case 'value': return finalize_value__type__text_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext_expression['type'], parent:Ctext_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cproperty__type__text_expression {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			text: () => application.Ctext__type__property
		}

		constructor(data:string, $this:Cproperty__type__text_expression) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.node_selection)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				text: cache((detach:boolean) => {
					const query__text_expression__type__property__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__text_expression__type__property__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'text') {
										return context.properties.type.state.node as application.Ctext__type__property;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dnode_selection extends Cnode_path {
		constructor(data:Tproperty__type__text_expression['node selection'], parent:Cproperty__type__text_expression) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'node selection')
		}
	}
}
export namespace Cvalue__type__text_expression {
	export class Dvalue extends Ctext_value {
		constructor(data:Tvalue__type__text_expression['value'], parent:Cvalue__type__text_expression) {
			super(data, parent, 'value')
		}
	}
}
export namespace Ctext_format {
	export class Dtype extends StateGroup<
		{ name: 'text', node:Ctext__type__text_format, init:Ttext__type__text_format}> {
		protected initializer(state: 'text') {
			switch (state) {
				case 'text': return (init:Ttext__type__text_format, parent:Ctext_format) => new Ctext__type__text_format(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'text') {
			switch (state) {
				case 'text': return finalize_text__type__text_format;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext_format['type'], parent:Ctext_format) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ctext_value {
	export class Dtype extends StateGroup<
		{ name: 'dynamic', node:Cdynamic__type__text_value, init:Tdynamic__type__text_value}|
		{ name: 'static', node:Cstatic__type__text_value, init:Tstatic__type__text_value}> {
		protected initializer(state: 'dynamic'|'static') {
			switch (state) {
				case 'dynamic': return (init:Tdynamic__type__text_value, parent:Ctext_value) => new Cdynamic__type__text_value(init, parent);
				case 'static': return (init:Tstatic__type__text_value, parent:Ctext_value) => new Cstatic__type__text_value(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic'|'static') {
			switch (state) {
				case 'dynamic': return finalize_dynamic__type__text_value;
				case 'static': return finalize_static__type__text_value;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext_value['type'], parent:Ctext_value) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdynamic__type__text_value {
	export class Dparameter extends Reference<parameters.Cparameters__,string> {
		public readonly inferences:{
			text: () => parameters.Ctext
		}

		constructor(data:string, $this:Cdynamic__type__text_value) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root.input.parameters)
				.then(context => {
					const entry = context?.properties.parameters.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				text: cache((detach:boolean) => {
					const query__text_value__type__dynamic__parameter_nval = $this.properties.parameter.ref;
					return resolve($this.properties.parameter.ref)
						.then(context => query__text_value__type__dynamic__parameter_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'text') {
								return resolve(context.properties.type.state.node as parameters.Ctext).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'parameter'`; }
	}
}
export namespace Cstatic__type__text_value {
}
export namespace Cquery {
	export class Dquery_type extends StateGroup<
		{ name: 'collection', node:Ccollection__query_type, init:Tcollection__query_type}|
		{ name: 'graph', node:Cgraph, init:Tgraph}> {
		protected initializer(state: 'collection'|'graph') {
			switch (state) {
				case 'collection': return (init:Tcollection__query_type, parent:Cquery) => new Ccollection__query_type(init, parent);
				case 'graph': return (init:Tgraph, parent:Cquery) => new Cgraph(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'graph') {
			switch (state) {
				case 'collection': return finalize_collection__query_type;
				case 'graph': return finalize_graph;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery['query type'], parent:Cquery) {
			super(data, parent, 'query type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'query type'`; }
	}
}
export namespace Ccollection__query_type {
	export class Dcollection_path extends Ccollection_selector {
		constructor(data:Tcollection__query_type['collection path'], parent:Ccollection__query_type) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context_node_path)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'collection path')
		}
	}
	export class Dcontext_node_path extends Cid_path {
		constructor(data:Tcollection__query_type['context node path'], parent:Ccollection__query_type) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.parameters)
					.then(context => context?.properties.context_type)
					.then(context => context?.component_root.output.node()).result!)
			}, 'context node path')
		}
	}
	export class Dhas_first extends StateGroup<
		{ name: 'no', node:Cno__has_first__collection, init:Tno__has_first__collection}|
		{ name: 'yes', node:Cyes__has_first__collection, init:Tyes__has_first__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_first__collection, parent:Ccollection__query_type) => new Cno__has_first__collection(init, parent);
				case 'yes': return (init:Tyes__has_first__collection, parent:Ccollection__query_type) => new Cyes__has_first__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_first__collection;
				case 'yes': return finalize_yes__has_first__collection;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__query_type['has first'], parent:Ccollection__query_type) {
			super(data, parent, 'has first');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has first'`; }
	}
	export class Dproperties extends AlanTopology<{ node:Cproperties__collection, init:Tproperties__collection},Ccollection__query_type,'order'> {
		protected graph_iterator(graph:('order')):(node:Cproperties__collection) => Cproperties__collection {
			switch (graph) {
				case 'order': return (entry:Cproperties__collection) => resolve(entry)
					.then(context => {
						if (context?.properties.has_next.state.name === 'yes') {
							return context.properties.has_next.state.node as query.Cyes__has_next__properties__collection;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.next.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Ccollection__query_type, key:string, entry_init:Tproperties__collection) { return new Cproperties__collection(key, entry_init, parent); }
		protected finalize = finalize_properties__collection
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tcollection__query_type['properties'], parent:Ccollection__query_type) {
			super(data, parent);
		}
	}
	export class Drecord_limit extends Cnumber_value {
		constructor(data:Tcollection__query_type['record limit'], parent:Ccollection__query_type) {
			super(data, parent, 'record limit')
		}
	}
}
export namespace Cfilters__collection {
	export class Dfilters extends AlanSet<{ node:Cfilters__filters, init:Tfilters__filters},Cfilters__collection> {
		protected initialize(parent:Cfilters__collection, entry_init:Tfilters__filters) { return new Cfilters__filters(entry_init, parent); }
		protected finalize = finalize_filters__filters
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filters'`; }
		constructor(data:Tfilters__collection['filters'], parent:Cfilters__collection) {
			super(data, parent);
		}
	}
	export class Dtodo_filter extends StateGroup<
		{ name: 'no', node:Cno__todo_filter, init:Tno__todo_filter}|
		{ name: 'yes', node:Cyes__todo_filter, init:Tyes__todo_filter}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__todo_filter, parent:Cfilters__collection) => new Cno__todo_filter(init, parent);
				case 'yes': return (init:Tyes__todo_filter, parent:Cfilters__collection) => new Cyes__todo_filter(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__todo_filter;
				case 'yes': return finalize_yes__todo_filter;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfilters__collection['todo filter'], parent:Cfilters__collection) {
			super(data, parent, 'todo filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'todo filter'`; }
	}
}
export namespace Cfilters__filters {
	export class Dpath extends Cnode_path {
		constructor(data:Tfilters__filters['path'], parent:Cfilters__filters) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'node', node:Cnode, init:Tnode}|
		{ name: 'property', node:Cproperty__type__filters, init:Tproperty__type__filters}> {
		protected initializer(state: 'node'|'property') {
			switch (state) {
				case 'node': return (init:Tnode, parent:Cfilters__filters) => new Cnode(init, parent);
				case 'property': return (init:Tproperty__type__filters, parent:Cfilters__filters) => new Cproperty__type__filters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'property') {
			switch (state) {
				case 'node': return finalize_node;
				case 'property': return finalize_property__type__filters;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfilters__filters['type'], parent:Cfilters__filters) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnode {
	export class Dtype extends StateGroup<
		{ name: 'containment', node:Ccontainment, init:Tcontainment}|
		{ name: 'existence', node:Cexistence, init:Texistence}> {
		protected initializer(state: 'containment'|'existence') {
			switch (state) {
				case 'containment': return (init:Tcontainment, parent:Cnode) => new Ccontainment(init, parent);
				case 'existence': return (init:Texistence, parent:Cnode) => new Cexistence(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'containment'|'existence') {
			switch (state) {
				case 'containment': return finalize_containment;
				case 'existence': return finalize_existence;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['type'], parent:Cnode) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccontainment {
	export class Doperator extends StateGroup<
		{ name: 'in', node:Cin, init:Tin}|
		{ name: 'not in', node:Cnot_in, init:Tnot_in}> {
		protected initializer(state: 'in'|'not in') {
			switch (state) {
				case 'in': return (init:Tin, parent:Ccontainment) => new Cin(init, parent);
				case 'not in': return (init:Tnot_in, parent:Ccontainment) => new Cnot_in(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'in'|'not in') {
			switch (state) {
				case 'in': return finalize_in;
				case 'not in': return finalize_not_in;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcontainment['operator'], parent:Ccontainment) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
	export class Dreferences extends Creference_filter_list {
		constructor(data:Tcontainment['references'], parent:Ccontainment) {
			super(data, parent, {
				entity_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.result_node())
					.then(context => context?.component_root.output.entity()).result!)
			}, 'references')
		}
	}
}
export namespace Cexistence {
	export class Doperator extends StateGroup<
		{ name: 'exists', node:Cexists, init:Texists}|
		{ name: 'not exists', node:Cnot_exists, init:Tnot_exists}> {
		protected initializer(state: 'exists'|'not exists') {
			switch (state) {
				case 'exists': return (init:Texists, parent:Cexistence) => new Cexists(init, parent);
				case 'not exists': return (init:Tnot_exists, parent:Cexistence) => new Cnot_exists(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'exists'|'not exists') {
			switch (state) {
				case 'exists': return finalize_exists;
				case 'not exists': return finalize_not_exists;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texistence['operator'], parent:Cexistence) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cproperty__type__filters {
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {

		constructor(data:string, $this:Cproperty__type__filters) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__property, init:Tnumber__type__property}|
		{ name: 'state group', node:Cstate_group, init:Tstate_group}> {
		protected initializer(state: 'number'|'state group') {
			switch (state) {
				case 'number': return (init:Tnumber__type__property, parent:Cproperty__type__filters) => new Cnumber__type__property(init, parent);
				case 'state group': return (init:Tstate_group, parent:Cproperty__type__filters) => new Cstate_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'state group') {
			switch (state) {
				case 'number': return finalize_number__type__property;
				case 'state group': return finalize_state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty__type__filters['type'], parent:Cproperty__type__filters) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumber__type__property {
	export class Dexpression extends Cnumber_filter_expression {
		constructor(data:Tnumber__type__property['expression'], parent:Cnumber__type__property) {
			super(data, parent, 'expression')
		}
	}
}
export namespace Cstate_group {
	export class Dstates_to_include extends AlanDictionary<{ node:Cstates_to_include, init:Tstates_to_include},Cstate_group> {
		protected initialize(parent:Cstate_group, key:string) { return new Cstates_to_include(key, {}, parent); }
		protected finalize = finalize_states_to_include
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states to include'`; }
		constructor(data:Tstate_group['states to include'], parent:Cstate_group) {
			super(data, parent);
		}
	}
}
export namespace Cyes__todo_filter {
	export class Dpath extends Cnode_path {
		public readonly inferences:{
			has_todo: () => application.Cyes__todo
		}
		constructor(data:Tyes__todo_filter['path'], parent:Cyes__todo_filter) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
			this.inferences = {
				has_todo: cache((detach:boolean) => {
					const query__query_type__collection__filters__todo_filter__yes__path_nval = this;
					return resolve(this)
						.then(context => query__query_type__collection__filters__todo_filter__yes__path_nval)
						.then(context => context?.component_root.output.result_node())
						.then(context => context?.properties.todo_definition)
						.then(context => {
							if (context?.properties.todo.state.name === 'yes') {
								return resolve(context.properties.todo.state.node as application.Cyes__todo).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Cyes__has_first__collection {
	export class Dfirst extends Reference<query.Cproperties__collection,string> {

		constructor(data:string, $this:Cyes__has_first__collection) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Cproperties__collection {
	export class Dhas_next extends StateGroup<
		{ name: 'no', node:Cno__has_next__properties__collection, init:Tno__has_next__properties__collection}|
		{ name: 'yes', node:Cyes__has_next__properties__collection, init:Tyes__has_next__properties__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next__properties__collection, parent:Cproperties__collection) => new Cno__has_next__properties__collection(init, parent);
				case 'yes': return (init:Tyes__has_next__properties__collection, parent:Cproperties__collection) => new Cyes__has_next__properties__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next__properties__collection;
				case 'yes': return finalize_yes__has_next__properties__collection;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__collection['has next'], parent:Cproperties__collection) {
			super(data, parent, 'has next');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type, init:Tcollection__type}|
		{ name: 'number', node:Cnumber__type__properties__collection, init:Tnumber__type__properties__collection}|
		{ name: 'stategroup', node:Cstategroup__type__properties__collection, init:Tstategroup__type__properties__collection}|
		{ name: 'text', node:Ctext__type__properties__collection, init:Ttext__type__properties__collection}> {
		protected initializer(state: 'collection'|'number'|'stategroup'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type, parent:Cproperties__collection) => new Ccollection__type(init, parent);
				case 'number': return (init:Tnumber__type__properties__collection, parent:Cproperties__collection) => new Cnumber__type__properties__collection(init, parent);
				case 'stategroup': return (init:Tstategroup__type__properties__collection, parent:Cproperties__collection) => new Cstategroup__type__properties__collection(init, parent);
				case 'text': return (init:Ttext__type__properties__collection, parent:Cproperties__collection) => new Ctext__type__properties__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'number'|'stategroup'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type;
				case 'number': return finalize_number__type__properties__collection;
				case 'stategroup': return finalize_stategroup__type__properties__collection;
				case 'text': return finalize_text__type__properties__collection;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__collection['type'], parent:Cproperties__collection) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_next__properties__collection {
	export class Dnext extends Reference<query.Cproperties__collection,string> {

		constructor(data:string, $this:Cyes__has_next__properties__collection) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.properties.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'next'`; }
	}
}
export namespace Ccollection__type {
	export class Dformat extends Ccollection_format {
		constructor(data:Tcollection__type['format'], parent:Ccollection__type) {
			super(data, parent, 'format')
		}
	}
	export class Dvalue extends Ccollection_expression {
		constructor(data:Tcollection__type['value'], parent:Ccollection__type) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.node()).result!)
			}, 'value')
		}
	}
}
export namespace Cnumber__type__properties__collection {
	export class Dformat extends Cnumber_format {
		constructor(data:Tnumber__type__properties__collection['format'], parent:Cnumber__type__properties__collection) {
			super(data, parent, 'format')
		}
	}
	export class Dvalue extends Cnumber_expression {
		constructor(data:Tnumber__type__properties__collection['value'], parent:Cnumber__type__properties__collection) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.node()).result!)
			}, 'value')
		}
	}
}
export namespace Cstategroup__type__properties__collection {
	export class Dformat extends Cstate_format {
		constructor(data:Tstategroup__type__properties__collection['format'], parent:Cstategroup__type__properties__collection) {
			super(data, parent, 'format')
		}
	}
	export class Dvalue extends Cstategroup_expression {
		constructor(data:Tstategroup__type__properties__collection['value'], parent:Cstategroup__type__properties__collection) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.node()).result!)
			}, 'value')
		}
	}
}
export namespace Ctext__type__properties__collection {
	export class Dformat extends Ctext_format {
		constructor(data:Ttext__type__properties__collection['format'], parent:Ctext__type__properties__collection) {
			super(data, parent, 'format')
		}
	}
	export class Dvalue extends Ctext_expression {
		constructor(data:Ttext__type__properties__collection['value'], parent:Ctext__type__properties__collection) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.node()).result!)
			}, 'value')
		}
	}
}
export namespace Cgraph {
	export class Dcontext_selection extends Cid_path {
		constructor(data:Tgraph['context selection'], parent:Cgraph) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.parameters)
					.then(context => context?.properties.context_type)
					.then(context => context?.component_root.output.node()).result!)
			}, 'context selection')
		}
	}
}
export namespace Centry_points {
	export class Dattribute extends Reference<application.Cattributes,string> {
		public readonly inferences:{
			collection: () => application.Ccollection__type__property,
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Centry_points) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.collection_selection)
				.then(context => context?.component_root.output.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				collection: cache((detach:boolean) => {
					const query__query_type__graph__entry_points__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(() => $this.properties.attribute.inferences.reference())
						.then(context => context?.properties.expression)
						.then(context => context?.component_root.output.collection()).result!;
				})
				,
				reference: cache((detach:boolean) => {
					const query__query_type__graph__entry_points__attribute_nval = $this.properties.attribute.ref;
					return resolve($this.properties.attribute.ref)
						.then(context => query__query_type__graph__entry_points__attribute_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as application.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'text') {
										return context.properties.type.state.node as application.Ctext__type__property;
									} else {
										return undefined;
									}
								})
								.then(context => {
									if (context?.properties.has_reference.state.name === 'yes') {
										return context.properties.has_reference.state.node as application.Cyes__has_reference;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dcollection_selection extends Ccollection_selector {
		constructor(data:Tentry_points['collection selection'], parent:Centry_points) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.context_selection)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'collection selection')
		}
	}
	export class Dgraph extends Reference<application.Cgraphs,string> {
		public readonly inferences:{
			acyclic: () => application.Cacyclic
		}

		constructor(data:string, $this:Centry_points) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.attribute?.inferences.collection())
				.then(context => context?.properties.graph_constraints)
				.then(context => {
					const entry = context?.properties.graphs.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				acyclic: cache((detach:boolean) => {
					const query__query_type__graph__entry_points__graph_nval = $this.properties.graph.ref;
					return resolve($this.properties.graph.ref)
						.then(context => query__query_type__graph__entry_points__graph_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'acyclic') {
								return resolve(context.properties.type.state.node as application.Cacyclic).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph'`; }
	}
}
export namespace Cquery__ {
	export class Dcollection_selection extends Ccollection_selector {
		constructor(data:Tquery__['collection selection'], parent:Cquery__) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.entry_points)
					.then(context => context?.properties.attribute?.inferences.reference())
					.then(context => context?.properties.expression)
					.then(context => context?.component_root.output.collection())
					.then(context => context?.properties.node).result!)
			}, 'collection selection')
		}
	}
	export class Dhas_first extends StateGroup<
		{ name: 'no', node:Cno__has_first__query, init:Tno__has_first__query}|
		{ name: 'yes', node:Cyes__has_first__query, init:Tyes__has_first__query}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_first__query, parent:Cquery__) => new Cno__has_first__query(init, parent);
				case 'yes': return (init:Tyes__has_first__query, parent:Cquery__) => new Cyes__has_first__query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_first__query;
				case 'yes': return finalize_yes__has_first__query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__['has first'], parent:Cquery__) {
			super(data, parent, 'has first');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has first'`; }
	}
	export class Dproperties extends AlanTopology<{ node:Cproperties__query, init:Tproperties__query},Cquery__,'order'> {
		protected graph_iterator(graph:('order')):(node:Cproperties__query) => Cproperties__query {
			switch (graph) {
				case 'order': return (entry:Cproperties__query) => resolve(entry)
					.then(context => {
						if (context?.properties.has_next.state.name === 'yes') {
							return context.properties.has_next.state.node as query.Cyes__has_next__properties__query;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.next.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cquery__, key:string, entry_init:Tproperties__query) { return new Cproperties__query(key, entry_init, parent); }
		protected finalize = finalize_properties__query
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tquery__['properties'], parent:Cquery__) {
			super(data, parent);
		}
	}
}
export namespace Cyes__has_first__query {
	export class Dfirst extends Reference<query.Cproperties__query,string> {

		constructor(data:string, $this:Cyes__has_first__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.properties.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Cproperties__query {
	export class Dhas_next extends StateGroup<
		{ name: 'no', node:Cno__has_next__properties__query, init:Tno__has_next__properties__query}|
		{ name: 'yes', node:Cyes__has_next__properties__query, init:Tyes__has_next__properties__query}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next__properties__query, parent:Cproperties__query) => new Cno__has_next__properties__query(init, parent);
				case 'yes': return (init:Tyes__has_next__properties__query, parent:Cproperties__query) => new Cyes__has_next__properties__query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next__properties__query;
				case 'yes': return finalize_yes__has_next__properties__query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__query['has next'], parent:Cproperties__query) {
			super(data, parent, 'has next');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next'`; }
	}
	export class Dpath extends Cnode_path {
		constructor(data:Tproperties__query['path'], parent:Cproperties__query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_selection)
					.then(context => context?.component_root.output.node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__properties__query, init:Tnumber__type__properties__query}|
		{ name: 'stategroup', node:Cstategroup__type__properties__query, init:Tstategroup__type__properties__query}|
		{ name: 'text', node:Ctext__type__properties__query, init:Ttext__type__properties__query}> {
		protected initializer(state: 'number'|'stategroup'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__properties__query, parent:Cproperties__query) => new Cnumber__type__properties__query(init, parent);
				case 'stategroup': return (init:Tstategroup__type__properties__query, parent:Cproperties__query) => new Cstategroup__type__properties__query(init, parent);
				case 'text': return (init:Ttext__type__properties__query, parent:Cproperties__query) => new Ctext__type__properties__query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'stategroup'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__properties__query;
				case 'stategroup': return finalize_stategroup__type__properties__query;
				case 'text': return finalize_text__type__properties__query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__query['type'], parent:Cproperties__query) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_next__properties__query {
	export class Dnext extends Reference<query.Cproperties__query,string> {

		constructor(data:string, $this:Cyes__has_next__properties__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.properties.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'next'`; }
	}
}
export namespace Cnumber__type__properties__query {
	export class Dformat extends Cnumber_format {
		constructor(data:Tnumber__type__properties__query['format'], parent:Cnumber__type__properties__query) {
			super(data, parent, 'format')
		}
	}
	export class Dnumber extends Reference<application.Cnumber__type__property,string> {

		constructor(data:string, $this:Cnumber__type__properties__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as application.Cnumber__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cstategroup__type__properties__query {
	export class Dformat extends Cstate_format {
		constructor(data:Tstategroup__type__properties__query['format'], parent:Cstategroup__type__properties__query) {
			super(data, parent, 'format')
		}
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstategroup__type__properties__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Ctext__type__properties__query {
	export class Dformat extends Ctext_format {
		constructor(data:Ttext__type__properties__query['format'], parent:Ctext__type__properties__query) {
			super(data, parent, 'format')
		}
	}
	export class Dtext extends Reference<application.Ctext__type__property,string> {

		constructor(data:string, $this:Ctext__type__properties__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
function finalize_property__type__collection_expression(obj:Cproperty__type__collection_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.attribute.inferences.collection)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.node_selection, detach);
	finalize_text_expression(obj.properties.property, detach);
}
function finalize_collection_expression(obj:Ccollection_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'property': finalize_property__type__collection_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_join(obj:Cjoin, detach:boolean = false) {
	finalize_text_value(obj.properties.separator, detach);
}
function finalize_collection_format(obj:Ccollection_format, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'join': finalize_join(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_step__collection_selector(obj:Cno__has_step__collection_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
}
function finalize_yes__has_step__collection_selector(obj:Cyes__has_step__collection_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.attribute.inferences.collection)(detach) !== undefined || detach);
	finalize_entity_descendant_selector(obj.properties.head, detach);
	finalize_collection_selector(obj.properties.tail, detach);
}
function finalize_collection_selector(obj:Ccollection_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	switch (obj.properties.has_step.state.name) {
		case 'no': finalize_no__has_step__collection_selector(obj.properties.has_step.state.node, detach); break;
		case 'yes': finalize_yes__has_step__collection_selector(obj.properties.has_step.state.node, detach); break;
	}
}
function finalize_no__has_step__entity_descendant_selector(obj:Cno__has_step__entity_descendant_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
}
function finalize_group__type__yes__has_step__entity_descendant_selector(obj:Cgroup__type__yes__has_step__entity_descendant_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.properties.attribute.inferences.group)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_step__entity_descendant_selector(obj:Cstate__type__yes__has_step__entity_descendant_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.attribute.inferences.stategroup)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_step__entity_descendant_selector(obj:Cyes__has_step__entity_descendant_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	finalize_entity_descendant_selector(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'group': finalize_group__type__yes__has_step__entity_descendant_selector(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_step__entity_descendant_selector(obj.properties.type.state.node, detach); break;
	}
}
function finalize_entity_descendant_selector(obj:Centity_descendant_selector, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	switch (obj.properties.has_step.state.name) {
		case 'no': finalize_no__has_step__entity_descendant_selector(obj.properties.has_step.state.node, detach); break;
		case 'yes': finalize_yes__has_step__entity_descendant_selector(obj.properties.has_step.state.node, detach); break;
	}
}
function finalize_no__has_step__id_path(obj:Cno__has_step__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
}
function finalize_collection_entry__type__yes__has_step(obj:Ccollection_entry__type__yes__has_step, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.attribute.inferences.collection)(detach) !== undefined || detach);
	finalize_text_value(obj.properties.entry_id, detach);
}
function finalize_group__type__yes__has_step__id_path(obj:Cgroup__type__yes__has_step__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.properties.attribute.inferences.group)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_step__id_path(obj:Cstate__type__yes__has_step__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.attribute.inferences.stategroup)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_step__id_path(obj:Cyes__has_step__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.node)(detach) !== undefined || detach);
	finalize_id_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'collection entry': finalize_collection_entry__type__yes__has_step(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__yes__has_step__id_path(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_step__id_path(obj.properties.type.state.node, detach); break;
	}
}
function finalize_id_path(obj:Cid_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_step.state.name) {
		case 'no': finalize_no__has_step__id_path(obj.properties.has_step.state.node, detach); break;
		case 'yes': finalize_yes__has_step__id_path(obj.properties.has_step.state.node, detach); break;
	}
}
function finalize_no__has_steps(obj:Cno__has_steps, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_collection_entry__type__yes__has_steps(obj:Ccollection_entry__type__yes__has_steps, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
}
function finalize_group__type__yes__has_steps(obj:Cgroup__type__yes__has_steps, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_parent(obj:Cparent, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.parent_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_reference(obj:Creference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.text.inferences.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.text.inferences.reference)(detach) !== undefined || detach);
}
function finalize_reference_rule(obj:Creference_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.property.inferences.reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.behaviour)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_steps(obj:Cstate__type__yes__has_steps, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_rule(obj:Cstate_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>obj.inferences.state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmandatory__type>obj.properties.rule.inferences.mandatory_reference)(detach) !== undefined || detach);
}
function finalize_yes__has_steps(obj:Cyes__has_steps, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'collection entry': finalize_collection_entry__type__yes__has_steps(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__yes__has_steps(obj.properties.type.state.node, detach); break;
		case 'parent': finalize_parent(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference(obj.properties.type.state.node, detach); break;
		case 'reference rule': finalize_reference_rule(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_steps(obj.properties.type.state.node, detach); break;
		case 'state rule': finalize_state_rule(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_path(obj:Cnode_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_property__type__number_expression(obj:Cproperty__type__number_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.properties.attribute.inferences.number)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.node_selection, detach);
}
function finalize_value__type__number_expression(obj:Cvalue__type__number_expression, detach:boolean = false) {
	finalize_number_value(obj.properties.value, detach);
}
function finalize_number_expression(obj:Cnumber_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'property': finalize_property__type__number_expression(obj.properties.type.state.node, detach); break;
		case 'value': finalize_value__type__number_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_equals(obj:Cequals, detach:boolean = false) {
	finalize_number_value(obj.properties.criterium, detach);
}
function finalize_no__greater_than(obj:Cno__greater_than, detach:boolean = false) {
}
function finalize_yes__greater_than(obj:Cyes__greater_than, detach:boolean = false) {
	finalize_number_value(obj.properties.criterium, detach);
}
function finalize_no__less_than(obj:Cno__less_than, detach:boolean = false) {
	assert((<(detach?:boolean) => query.Cyes__greater_than>obj.inferences.must_be_greater_than)(detach) !== undefined || detach);
}
function finalize_yes__less_than(obj:Cyes__less_than, detach:boolean = false) {
	finalize_number_value(obj.properties.criterium, detach);
}
function finalize_range(obj:Crange, detach:boolean = false) {
	switch (obj.properties.greater_than.state.name) {
		case 'no': finalize_no__greater_than(obj.properties.greater_than.state.node, detach); break;
		case 'yes': finalize_yes__greater_than(obj.properties.greater_than.state.node, detach); break;
	}
	switch (obj.properties.less_than.state.name) {
		case 'no': finalize_no__less_than(obj.properties.less_than.state.node, detach); break;
		case 'yes': finalize_yes__less_than(obj.properties.less_than.state.node, detach); break;
	}
}
function finalize_number_filter_expression(obj:Cnumber_filter_expression, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'equals': finalize_equals(obj.properties.operator.state.node, detach); break;
		case 'range': finalize_range(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_date(obj:Cdate, detach:boolean = false) {
}
function finalize_datetime(obj:Cdatetime, detach:boolean = false) {
}
function finalize_decimal(obj:Cdecimal, detach:boolean = false) {
}
function finalize_number__type__number_format(obj:Cnumber__type__number_format, detach:boolean = false) {
}
function finalize_time(obj:Ctime, detach:boolean = false) {
}
function finalize_number_format(obj:Cnumber_format, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'date': finalize_date(obj.properties.type.state.node, detach); break;
		case 'datetime': finalize_datetime(obj.properties.type.state.node, detach); break;
		case 'decimal': finalize_decimal(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__number_format(obj.properties.type.state.node, detach); break;
		case 'time': finalize_time(obj.properties.type.state.node, detach); break;
	}
}
function finalize_dynamic__type__number_value(obj:Cdynamic__type__number_value, detach:boolean = false) {
	assert((<(detach?:boolean) => parameters.Cparameters__>(obj.properties.parameter as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => parameters.Cnumber>obj.properties.parameter.inferences.number)(detach) !== undefined || detach);
}
function finalize_static__type__number_value(obj:Cstatic__type__number_value, detach:boolean = false) {
}
function finalize_number_value(obj:Cnumber_value, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'dynamic': finalize_dynamic__type__number_value(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static__type__number_value(obj.properties.type.state.node, detach); break;
	}
}
function finalize_query_context(obj:Cquery_context, detach:boolean = false) {
}
function finalize_root(obj:Croot, detach:boolean = false) {
}
function finalize_no__has_alternative(obj:Cno__has_alternative, detach:boolean = false) {
}
function finalize_yes__has_alternative(obj:Cyes__has_alternative, detach:boolean = false) {
	finalize_reference_filter_list(obj.properties.alternative, detach);
}
function finalize_reference_filter_list(obj:Creference_filter_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.input.entity_node)(detach) !== undefined || detach);
	switch (obj.properties.context.state.name) {
		case 'query context': finalize_query_context(obj.properties.context.state.node, detach); break;
		case 'root': finalize_root(obj.properties.context.state.node, detach); break;
	}
	switch (obj.properties.has_alternative.state.name) {
		case 'no': finalize_no__has_alternative(obj.properties.has_alternative.state.node, detach); break;
		case 'yes': finalize_yes__has_alternative(obj.properties.has_alternative.state.node, detach); break;
	}
	finalize_id_path(obj.properties.referenced_node, detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.properties.referenced_node.inferences.entity_constraint)(detach) !== undefined || detach);
}
function finalize_text__type__state_format(obj:Ctext__type__state_format, detach:boolean = false) {
}
function finalize_state_format(obj:Cstate_format, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'text': finalize_text__type__state_format(obj.properties.type.state.node, detach); break;
	}
}
function finalize_stategroup_expression(obj:Cstategroup_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.output.stategroup)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>obj.properties.attribute.inferences.stategroup)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.node_selection, detach);
}
function finalize_property__type__text_expression(obj:Cproperty__type__text_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.properties.attribute.inferences.text)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.node_selection, detach);
}
function finalize_value__type__text_expression(obj:Cvalue__type__text_expression, detach:boolean = false) {
	finalize_text_value(obj.properties.value, detach);
}
function finalize_text_expression(obj:Ctext_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'property': finalize_property__type__text_expression(obj.properties.type.state.node, detach); break;
		case 'value': finalize_value__type__text_expression(obj.properties.type.state.node, detach); break;
	}
}
function finalize_text__type__text_format(obj:Ctext__type__text_format, detach:boolean = false) {
}
function finalize_text_format(obj:Ctext_format, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'text': finalize_text__type__text_format(obj.properties.type.state.node, detach); break;
	}
}
function finalize_dynamic__type__text_value(obj:Cdynamic__type__text_value, detach:boolean = false) {
	assert((<(detach?:boolean) => parameters.Cparameters__>(obj.properties.parameter as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => parameters.Ctext>obj.properties.parameter.inferences.text)(detach) !== undefined || detach);
}
function finalize_static__type__text_value(obj:Cstatic__type__text_value, detach:boolean = false) {
}
function finalize_text_value(obj:Ctext_value, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'dynamic': finalize_dynamic__type__text_value(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static__type__text_value(obj.properties.type.state.node, detach); break;
	}
}
function finalize_in(obj:Cin, detach:boolean = false) {
}
function finalize_not_in(obj:Cnot_in, detach:boolean = false) {
}
function finalize_containment(obj:Ccontainment, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'in': finalize_in(obj.properties.operator.state.node, detach); break;
		case 'not in': finalize_not_in(obj.properties.operator.state.node, detach); break;
	}
	finalize_reference_filter_list(obj.properties.references, detach);
}
function finalize_exists(obj:Cexists, detach:boolean = false) {
}
function finalize_not_exists(obj:Cnot_exists, detach:boolean = false) {
}
function finalize_existence(obj:Cexistence, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'exists': finalize_exists(obj.properties.operator.state.node, detach); break;
		case 'not exists': finalize_not_exists(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_node(obj:Cnode, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'containment': finalize_containment(obj.properties.type.state.node, detach); break;
		case 'existence': finalize_existence(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number__type__property(obj:Cnumber__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.inferences.number_type)(detach) !== undefined || detach);
	finalize_number_filter_expression(obj.properties.expression, detach);
}
function finalize_states_to_include(obj:Cstates_to_include, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_group(obj:Cstate_group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states_to_include) {
		finalize_states_to_include(entry, detach);
	}
	if (!detach) {
		if (obj.properties.states_to_include.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
}
function finalize_property__type__filters(obj:Cproperty__type__filters, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__property(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group(obj.properties.type.state.node, detach); break;
	}
}
function finalize_filters__filters(obj:Cfilters__filters, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	switch (obj.properties.type.state.name) {
		case 'node': finalize_node(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property__type__filters(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__todo_filter(obj:Cno__todo_filter, detach:boolean = false) {
}
function finalize_yes__todo_filter(obj:Cyes__todo_filter, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cyes__todo>obj.properties.path.inferences.has_todo)(detach) !== undefined || detach);
}
function finalize_filters__collection(obj:Cfilters__collection, detach:boolean = false) {
	for (const entry of obj.properties.filters) {
		finalize_filters__filters(entry, detach);
	}
	if (!detach) {
	}
	switch (obj.properties.todo_filter.state.name) {
		case 'no': finalize_no__todo_filter(obj.properties.todo_filter.state.node, detach); break;
		case 'yes': finalize_yes__todo_filter(obj.properties.todo_filter.state.node, detach); break;
	}
}
function finalize_no__has_first__collection(obj:Cno__has_first__collection, detach:boolean = false) {
}
function finalize_yes__has_first__collection(obj:Cyes__has_first__collection, detach:boolean = false) {
	assert((<(detach?:boolean) => query.Cproperties__collection>(obj.properties.first as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_next__properties__collection(obj:Cno__has_next__properties__collection, detach:boolean = false) {
}
function finalize_yes__has_next__properties__collection(obj:Cyes__has_next__properties__collection, detach:boolean = false) {
	assert((<(detach?:boolean) => query.Cproperties__collection>(obj.properties.next as any).resolve)(detach) !== undefined || detach);
}
function finalize_collection__type(obj:Ccollection__type, detach:boolean = false) {
	finalize_collection_format(obj.properties.format, detach);
	finalize_collection_expression(obj.properties.value, detach);
}
function finalize_number__type__properties__collection(obj:Cnumber__type__properties__collection, detach:boolean = false) {
	finalize_number_format(obj.properties.format, detach);
	finalize_number_expression(obj.properties.value, detach);
}
function finalize_stategroup__type__properties__collection(obj:Cstategroup__type__properties__collection, detach:boolean = false) {
	finalize_state_format(obj.properties.format, detach);
	finalize_stategroup_expression(obj.properties.value, detach);
}
function finalize_text__type__properties__collection(obj:Ctext__type__properties__collection, detach:boolean = false) {
	finalize_text_format(obj.properties.format, detach);
	finalize_text_expression(obj.properties.value, detach);
}
function finalize_properties__collection(obj:Cproperties__collection, detach:boolean = false) {
	switch (obj.properties.has_next.state.name) {
		case 'no': finalize_no__has_next__properties__collection(obj.properties.has_next.state.node, detach); break;
		case 'yes': finalize_yes__has_next__properties__collection(obj.properties.has_next.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__properties__collection(obj.properties.type.state.node, detach); break;
		case 'stategroup': finalize_stategroup__type__properties__collection(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties__collection(obj.properties.type.state.node, detach); break;
	}
}
function finalize_collection__query_type(obj:Ccollection__query_type, detach:boolean = false) {
	finalize_collection_selector(obj.properties.collection_path, detach);
	finalize_id_path(obj.properties.context_node_path, detach);
	finalize_filters__collection(obj.properties.filters, detach);
	switch (obj.properties.has_first.state.name) {
		case 'no': finalize_no__has_first__collection(obj.properties.has_first.state.node, detach); break;
		case 'yes': finalize_yes__has_first__collection(obj.properties.has_first.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__collection(entry, detach);
	}
	if (!detach) {
	}
	finalize_number_value(obj.properties.record_limit, detach);
}
function finalize_entry_points(obj:Centry_points, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.properties.attribute.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.attribute.inferences.reference)(detach) !== undefined || detach);
	finalize_collection_selector(obj.properties.collection_selection, detach);
	assert((<(detach?:boolean) => application.Cgraphs>(obj.properties.graph as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cacyclic>obj.properties.graph.inferences.acyclic)(detach) !== undefined || detach);
}
function finalize_no__has_first__query(obj:Cno__has_first__query, detach:boolean = false) {
}
function finalize_yes__has_first__query(obj:Cyes__has_first__query, detach:boolean = false) {
	assert((<(detach?:boolean) => query.Cproperties__query>(obj.properties.first as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_next__properties__query(obj:Cno__has_next__properties__query, detach:boolean = false) {
}
function finalize_yes__has_next__properties__query(obj:Cyes__has_next__properties__query, detach:boolean = false) {
	assert((<(detach?:boolean) => query.Cproperties__query>(obj.properties.next as any).resolve)(detach) !== undefined || detach);
}
function finalize_number__type__properties__query(obj:Cnumber__type__properties__query, detach:boolean = false) {
	finalize_number_format(obj.properties.format, detach);
	assert((<(detach?:boolean) => application.Cnumber__type__property>(obj.properties.number as any).resolve)(detach) !== undefined || detach);
}
function finalize_stategroup__type__properties__query(obj:Cstategroup__type__properties__query, detach:boolean = false) {
	finalize_state_format(obj.properties.format, detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__properties__query(obj:Ctext__type__properties__query, detach:boolean = false) {
	finalize_text_format(obj.properties.format, detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_properties__query(obj:Cproperties__query, detach:boolean = false) {
	switch (obj.properties.has_next.state.name) {
		case 'no': finalize_no__has_next__properties__query(obj.properties.has_next.state.node, detach); break;
		case 'yes': finalize_yes__has_next__properties__query(obj.properties.has_next.state.node, detach); break;
	}
	finalize_node_path(obj.properties.path, detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__properties__query(obj.properties.type.state.node, detach); break;
		case 'stategroup': finalize_stategroup__type__properties__query(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties__query(obj.properties.type.state.node, detach); break;
	}
}
function finalize_query__(obj:Cquery__, detach:boolean = false) {
	finalize_collection_selector(obj.properties.collection_selection, detach);
	switch (obj.properties.has_first.state.name) {
		case 'no': finalize_no__has_first__query(obj.properties.has_first.state.node, detach); break;
		case 'yes': finalize_yes__has_first__query(obj.properties.has_first.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__query(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_graph(obj:Cgraph, detach:boolean = false) {
	finalize_id_path(obj.properties.context_selection, detach);
	finalize_entry_points(obj.properties.entry_points, detach);
	finalize_query__(obj.properties.query, detach);
}
function finalize_query(obj:Cquery, detach:boolean = false) {
	switch (obj.properties.query_type.state.name) {
		case 'collection': finalize_collection__query_type(obj.properties.query_type.state.node, detach); break;
		case 'graph': finalize_graph(obj.properties.query_type.state.node, detach); break;
	}
}

export namespace Cquery {
	export function create(init:Tquery, input: {
	'application':application.Capplication
	'parameters':parameters.Cparameters}, symbols_file?: SymbolsFile):Cquery {
		const instance = new Cquery(init, input as any, symbols_file);
		return instance;
	};
}
